import './sales-surface.css';
import { fillPath, readJsonPath, rowMatches } from '../predicate.js';
import { navigateFromFrame } from '../navigate-seam.js';
import { recordAddress, type Destination } from '../record-address.js';
// CAOS-1908 — a `controlPlane` page's roster is drawn by the SAME collection surface a roster page
// uses, and narrowed by the SAME tab helpers, rather than by a second toolbar beside them. See
// {@link renderControlPlaneCollection} for what is and is not shared.
import { renderCollection } from './collection-surface.js';
import { buildFilterTabs, canNarrow, rowMatchesFilter } from './roster-page.js';
import type {
  ActionDestinationMeta,
  ComponentBundleMeta,
  ObjectMeta,
  OrgMetadata,
  PageComponentItemMeta,
  PageEmbedItemMeta,
  PageFieldItemMeta,
  PageMeta,
  PageReadMeta,
  PageRowActionMeta,
  PageCollectedFieldMeta,
  RouteVerbRefMeta,
  PageWriteMeta,
  PageSectionMeta,
  PageUnknownItemMeta,
} from '../metadata-source.js';

/**
 * CAOS-498 — how many pages deep an embed chain is drawn before the surface refuses in words.
 *
 * Deploy refuses a cycle outright, so a page reaching this limit is metadata that predates that
 * gate. Four is chosen to be past any composition anybody would author on purpose — the case the
 * ticket exists for is one page carrying one other — while still stopping long before a browser
 * does anything a person would experience as a hang.
 */
const MAX_EMBED_DEPTH = 4;

/**
 * CAOS-1908 — the column key the per-row action buttons occupy on a page roster drawn through the
 * collection surface.
 *
 * Prefixed for the same reason {@link ROW_ACTION_COLUMN} is on a roster page: a page's columns come
 * from the package author, and one that happened to declare a column called `actions` would have its
 * cells silently overwritten by the buttons. The bare-table branch keeps its own historical
 * `__actions` key, because that key is already on screen in every org running a setup page today and
 * renaming it would be a change to existing behaviour this ticket has no reason to make.
 */
const PAGE_ROSTER_ACTION_COLUMN = '__pageRosterAction';

/**
 * CAOS-388 — the PAGE surface: a screen drawn from what its page says it contains.
 *
 * A page has been authorable for a long time and has never drawn anything. It passed validation, it
 * installed, it deployed — and then nothing in the workspace read its sections, so every screen we
 * have was written as code instead. This module is the reading: the sections a page declares, and
 * inside each of them the fields and the PIECES the author placed, drawn in the order they were
 * placed in.
 *
 * ── Nothing here is ever silently empty ──────────────────────────────────────
 * The whole reason a page could be deployed drawing nothing is that "nothing to draw" and "drew
 * nothing" looked identical from outside. So every way this can come up short says so on the screen,
 * in the words a person would use:
 *
 *   • a page that never said what it contains        — says so, and is not the same message as
 *   • a page that said it contains nothing           — which says that instead;
 *   • a piece placed that this workspace has not got — names the piece, and says it is not installed;
 *   • a piece installed whose code cannot be loaded  — names it, and says so, only after CAOS-577's
 *                                                       loader has actually tried and failed to fetch
 *                                                       it from where the manifest says it lives;
 *   • a field placed that its record type has not got — names the field and the record type.
 *
 * The distinction between the first two is carried all the way from the projection on purpose: they
 * are different statements about the page, and a person reading the screen would do a different
 * thing about each.
 */

/** What the page renderer needs to know beyond the page itself. */
export interface PageRenderOptions {
  /** The org's installed metadata — where a placed piece and a placed field are resolved. */
  readonly meta: OrgMetadata;
  /** The app the page is being read inside, for the header's eyebrow. */
  readonly appLabel?: string;
  /**
   * The record open behind this page, when there is one. A page bound to a record type that is
   * opened without a record still draws — it draws the fields' names with no values, which is what
   * a layout looks like when nothing is loaded into it.
   */
  readonly record?: Record<string, unknown> & { display?: Record<string, unknown> };
  /**
   * Whether a custom element tag is actually registered in this workspace. Injected so the "the
   * piece is installed but its code is not here" state can be exercised deterministically; in
   * production it is the browser's own registry.
   */
  readonly isTagRegistered?: (tag: string) => boolean;
  /**
   * CAOS-577 — fetch and load a component's code from where its manifest's `bundle` names it.
   * Called only when a placed piece's tag is not already registered. Injected for the same reason
   * `isTagRegistered` is: a real fetch is not something a unit test should have to perform, and a
   * component that proves this mechanism has to be one no first-party bundle already satisfies. In
   * production this is a real dynamic `import()` — see {@link defaultLoadComponentModule}.
   */
  readonly loadComponentModule?: (bundle: ComponentBundleMeta, tag: string) => Promise<void>;
  /**
   * CAOS-583 — the route this page is open at, so the header can offer an inline affordance to this
   * screen's own help topic when one is anchored to it. Absent ⇒ falls back to `location.pathname`.
   */
  readonly pathname?: string;
  /**
   * CAOS-498 — TRUE when this page is being drawn INSIDE another page rather than as the
   * destination the reader opened.
   *
   * This is the ticket's third criterion, and it is a fact the drawing layer is TOLD rather than one
   * the page can work out. A page cannot know it is embedded by looking at its own body: the same
   * page is a destination in one place and an item in another, and it is the same page both times.
   *
   * What it changes is ownership of the frame. A page drawn as a destination writes the screen's
   * heading; the same page drawn as an item must not, because the page around it already did — two
   * headings, the outer one naming the destination and the inner one naming a different application's
   * surface, reads as a broken screen rather than as composition. So an embedded page draws its own
   * body and leaves the chrome to its host.
   */
  readonly embedded?: boolean;
  /**
   * CAOS-498 — how many embeds deep this render already is. Internal; callers pass nothing.
   *
   * A cycle is refused at DEPLOY (`validatePageEmbeds`), which is where an authoring mistake should
   * be caught and named. This is the second line, and it exists for the case deploy cannot cover:
   * metadata installed by an org running an older kernel, before that gate existed. It REFUSES IN
   * WORDS at the limit rather than silently stopping — a surface that quietly ends mid-way is the
   * silent blank every notice in this file exists to prevent, and it would be indistinguishable from
   * a page that legitimately holds nothing.
   */
  readonly embedDepth?: number;
  /**
   * CAOS-744 — the control-plane fetch seam a `controlPlane`-kind page's read/write routes go
   * through. Injected for the same reason `isTagRegistered`/`loadComponentModule` are: a real fetch
   * is not something a unit test should have to perform. Defaults to a same-origin `fetch`.
   *
   * DELIBERATELY the ONLY thing that turns a route name into an address, and it is given nothing but
   * a path and an optional method/body — see {@link renderControlPlaneSurface}'s doc for why that is
   * the security boundary, not an implementation detail.
   */
  /**
   * CAOS-798 — the NAMED parameters of the route this page is open at, so a placed component can be
   * told which record it was opened for.
   *
   * `kernel/src/meta/component.ts` has always let an input declare `source: "urlParameter"` — "a
   * named parameter of the route that opened the surface" — and nothing ever resolved one: the
   * string appeared in that enum and in the generated registry, and nowhere in this workspace. A
   * placed component's props came from the page author's static `props`, `valueAt`, and
   * `columns`, none of which can carry the identity of the thing on screen. So a per-record host
   * surface could not become a package-shipped component, however well its code was written.
   *
   * `pathname` above is the raw route and deliberately stays that: the segment-to-name mapping
   * belongs to whatever matched the route, not to this renderer guessing at positions.
   */
  readonly routeParams?: Readonly<Record<string, string>>;
  /**
   * CAOS-1809 — the facts about the PERSON READING this page, so a placed component can be told who
   * they are. Keyed by the input names a manifest uses for them, which is what makes it readable by
   * the same name-is-the-key rule `routeParams` above is read by; built by
   * {@link viewerInputContext} from the shell runtime's own viewer, never assembled here.
   *
   * ABSENT IS NOT EMPTY, and the difference is the state this exists for. A caller that supplies
   * nothing — a surface drawn before sign-in, or a test — leaves every `userContext` input off the
   * element rather than setting it blank, which is exactly what `caos-topbar` documents as "a bar
   * above a sign-in screen". A session with a blank name is a different fact and stays one.
   *
   * WHY IT IS INJECTED rather than read from the session here: every other viewer-shaped value on
   * this interface is (`heldPermissions` reaches the surface context the same way, CAOS-1624). This
   * module draws; who is signed in is a fact about the whole shell, resolved once at boot from
   * `auth whoami` and not re-derived per surface.
   */
  readonly viewer?: Readonly<Record<string, string>>;
  readonly controlPlaneFetch?: (
    path: string,
    init?: { method?: string; body?: string },
  ) => Promise<{ ok: boolean; json(): Promise<unknown> }>;
  /**
   * CAOS-1616 — where each object's records are reached from here, so a roster row action carrying
   * a `destination` can be turned into an address.
   *
   * Passed in as a resolved map rather than computed here, for the reason every other seam on this
   * interface is injected: this module draws, and the app/tab an object's records live at is a fact
   * about the whole installed shell plan, not about this page. Absent is not empty — a caller that
   * supplies nothing gets an action that is still DRAWN and says it cannot be reached, never one
   * that is silently dropped, which is the failure CAOS-1616 exists to remove.
   */
  readonly objectHomes?: ReadonlyMap<string, Destination>;
  /**
   * CAOS-1767 — how a `list`-kind page is drawn, and the fetch its read goes through.
   *
   * `listDraw` IS HOW THE LIST ARRIVES, and it has no default here. The list surface lives in
   * `content-source.ts`, which already imports this file, so this module cannot reach it; the real
   * drawing is supplied in exactly one place, `renderPage` (`content-source.ts`), which every
   * surface should call instead of `renderPageSurface`. A list page reaching this function without
   * it draws a notice saying this screen does not draw a list — see the branch itself for why that
   * is a sentence rather than a silent fallback.
   *
   * The seam is also what a test substitutes to exercise the branch without a list surface, which is
   * the other reason it is a seam rather than an import.
   *
   * `listFetch` is the same kind of injection `controlPlaneFetch` is, and a DIFFERENT one on
   * purpose: a list page reads the Data API through the host's ordinary record-read path, which
   * takes a real `fetch` and a real `Response`, while `controlPlaneFetch` is narrowed to a path and
   * `{ ok, json() }` because that is all a route-shaped page is allowed to say. Folding them into
   * one seam would have widened the narrow one.
   */
  readonly listDraw?: (page: PageMeta, options: PageRenderOptions) => Promise<HTMLElement>;
  readonly listFetch?: typeof fetch;
  /**
   * CAOS-492 — how a ROSTER embedded in a section arrives, on exactly the seam CAOS-1767 established
   * for a list page one ticket earlier.
   *
   * A SEAM RATHER THAN AN IMPORT, for the same structural reason `listDraw` is one: the roster
   * destination lives in `surfaces/roster-destination.ts`, which is reached from `content-source.ts`,
   * and `content-source.ts` already imports this file. Drawing it here directly is also the wrong
   * division — a roster resolves its spec through `/api/metadata/component-get` and then reads its own
   * rows, neither of which is this module's business. The real drawing is supplied in one place,
   * `renderPage` (`content-source.ts`), which is why every surface should call that rather than
   * `renderPageSurface`.
   *
   * ABSENT IS A SENTENCE, NOT A SHRUG, exactly as the list branch is: an embed that reaches here
   * without the seam says this screen does not draw a list, rather than leaving the gap that would be
   * indistinguishable from a roster with no rows. A test substitutes it to exercise the branch
   * without a roster surface, which is the other reason it is a seam.
   */
  readonly embedRosterDraw?: (rosterPageKey: string, options: PageRenderOptions) => HTMLElement;
}

/** The default control-plane fetch: same-origin, credentials included, nothing else. */
const defaultControlPlaneFetch: NonNullable<PageRenderOptions['controlPlaneFetch']> = (path, init) =>
  fetch(path, { credentials: 'same-origin', ...(init ?? {}) });


/** The key a streaming page stamps its "populated" promise under — mirrors `content-source.ts`'s
 *  `CONTENT_SETTLED` convention (same string, so `whenContentSettled` can await either). */
const CONTENT_SETTLED = '__caosContentSettled';

/** The browser's own answer to "is this element's code present?" */
function defaultIsTagRegistered(tag: string): boolean {
  return typeof customElements !== 'undefined' && customElements.get(tag) !== undefined;
}

/**
 * CAOS-577 — the real loader: fetch and load a component's code from where its manifest says it
 * lives, rather than from whatever the workspace happened to be built with.
 *
 * `bundle.module` is resolved against THIS WORKSPACE'S OWN ORIGIN and nowhere else. That is a
 * deliberate, narrow line, not an oversight to widen later without a decision: this page's
 * Content-Security-Policy ships `script-src 'self'` (see `docs/architecture/CAOS-612-map-content-
 * security-policy.md`), and the recorded reasoning behind it — "no third party's code executes in
 * our page, for any feature, ever" — applies to a placed component's bundle exactly as it applies
 * to a map provider's. Refusing a cross-origin module here, before a fetch is even attempted,
 * produces the "cannot be drawn" state on purpose; it does not depend on the browser's own CSP
 * enforcement of dynamic `import()` to catch it as a side effect.
 *
 * What this function deliberately does NOT decide: whether a component's code should ever be
 * allowed to load from somewhere other than this origin — a subscriber's own hosting, or a
 * package CDN — is a real policy question (trusted origins, subresource integrity, whether every
 * installed package's code is implicitly trusted) that this ticket does not answer and that this
 * function does not pre-empt. It refuses the wider case rather than quietly choosing an answer for
 * it. See CAOS-577's closing report for the open question this leaves for the platform architect.
 *
 * Exported (unlike {@link defaultIsTagRegistered}) so the same-origin refusal itself — the one
 * security-relevant thing this function decides on its own — has a direct unit test rather than
 * only being exercised incidentally through `renderPageSurface`.
 */
/**
 * Resolve a manifest's `bundle.module` to the URL this workspace will fetch it from.
 *
 * A relative `module` is relative to the workspace ROOT, never to the page the piece happens to be
 * placed on. Every first-party manifest writes one — `components/field_editor/field-editor.js`,
 * `components/accordion.js` — and the package that ships it serves those files from the root, so
 * the path means the same thing wherever the piece is placed. Resolving it against
 * `location.href` instead made the fetch depend on the route: on `/` the two agree, and on
 * `/setup/objects/task/new-field` the workspace asked for
 * `/setup/objects/task/components/field_editor/field-editor.js` and drew "the code that draws it
 * could not be loaded" for a bundle sitting correctly at the root. That is how this was found — the
 * first placed component the host does not also compile in, which is the only shape that reaches
 * this function at all (see `kernel/src/meta/component.ts` on `module`).
 *
 * An ALREADY-ABSOLUTE `module` is parsed on its own, with no base, for two reasons: the base is
 * irrelevant to the result, and a non-hierarchical absolute specifier (`data:`, which CAOS-577's
 * end-to-end test loads a real module from) has no root to resolve against — asking for one throws
 * before the same-origin check below can make its decision. The root base is therefore built only
 * for a relative `module`, which is the only case that needs one.
 *
 * It can still throw: a RELATIVE module on a page that itself has no root — `data:`, `about:blank` —
 * has nowhere to resolve to, and `new URL('/', …)` says so with a bare "Invalid URL". That is the
 * same failure the previous resolution gave, and it fails closed: a module that cannot be resolved
 * is a module that is not loaded, and the page draws the "cannot be drawn here" notice. It is called
 * out because the message is unhelpful, not because the behaviour is wrong.
 *
 * Exported for the same reason the loader itself is: this is the whole of what resolution decides,
 * and the real loader's own `import()` cannot be observed from a test, so the resolved URL is only
 * directly assertable here.
 */
export function resolveBundleModule(module: string): URL {
  try {
    return new URL(module);
  } catch {
    // Not absolute, so it is relative to this workspace's root. `new URL('/', location.href)` is
    // that root as a genuine absolute URL — `location.origin` is a bare string ("http://host:port",
    // or the opaque "null") and is not guaranteed to be parseable as the base the URL constructor
    // requires.
    return new URL(module, new URL('/', location.href));
  }
}

export async function defaultLoadComponentModule(bundle: ComponentBundleMeta, tag: string): Promise<void> {
  if (typeof location === 'undefined') {
    throw new Error(`cannot load the code for "${tag}" — there is no browser context to fetch it in`);
  }
  const resolved = resolveBundleModule(bundle.module);
  if (resolved.origin !== location.origin) {
    throw new Error(
      `refusing to load "${tag}" from ${resolved.origin} — this workspace only loads component code from its own origin`,
    );
  }
  await import(/* @vite-ignore */ resolved.href);
}

/**
 * A loud notice in place of something that could not be drawn. `alert` styling when something is
 * genuinely broken (a piece that is missing or unusable), `status` when the page simply has not been
 * filled in yet — the second is not a fault and must not be presented as one.
 *
 * Deliberately NOT a live region. Every one of these is present at first paint, and `role="alert"` /
 * `role="status"` both announce on insertion — so a page missing three pieces fired three
 * interruptions at a screen-reader user on load, for text that was already sitting on the page for
 * them to read in document order. The severity is carried visually and by the heading text instead.
 */
/**
 * CAOS-1857 — the readable sentence out of a `{ ok: false, error }` body, or null when there is not
 * one to show.
 *
 * ── WHICH SENTENCE, AND WHY NOT `message` FIRST ────────────────────────────────────────────────────
 * `details.reason` is preferred over `message` because of what each one is. `message` is rendered
 * from a localizable template keyed by `code` (`errors/catalog.ts`), so for a whole class of refusals
 * it is the GENERIC sentence for that code — "You do not have permission to do that" — while
 * `details.reason` is the specific thing this route decided. On `account/orgs` that is the difference
 * between a sentence a customer can act on and one that only restates the status.
 *
 * `message` is the fallback rather than ignored: plenty of refusals carry no `reason`, and the
 * catalogue sentence is then the best there is. When neither is a usable string — or the class is not
 * one of the five below — this returns null and the caller falls back to the connectivity notice,
 * which is the honest answer for a transport failure or a body that is not an envelope at all.
 *
 * ── ONLY THE CLASSES THAT ARE A DECISION ABOUT THE REQUEST ─────────────────────────────────────────
 * The class gates WHETHER a sentence is drawn at all, not just which heading it gets. The five
 * allowed classes are the ones where the kernel decided something about what was asked — a
 * permission, a missing thing, a malformed request, a clash, a limit — and that decision is the
 * answer, so saying it is strictly better than guessing at the network.
 *
 * The rest (`internal`, `integration`, `deploy`, `computation`) are faults rather than decisions, and
 * their catalogue sentences are not written for someone looking at a screen: `internal.unexpected`
 * reads "Something went wrong on our end. Reference <id> — we're looking into it. Run `caos trace
 * show <id> -o <org>` to see the full diagnosis." Drawn in a browser that instructs an org admin to
 * run a command line they may not have, about a fault they cannot diagnose. Those keep the notice
 * they already had, where "try again" is at least sound advice for a transient fault. Narrowing here
 * also keeps this change to the thing CAOS-1857 is about: a refusal that explained itself and was
 * reported as a connectivity problem. A fault is not that.
 *
 * ── `denied` IS ABOUT THE TITLE ────────────────────────────────────────────────────────────────────
 * Within the allowed classes it only picks which heading reads true: "not available to you" for a
 * permission or not-found refusal, "couldn't load" for the other three. Nothing is permitted or
 * refused here — the route already decided that; this only reports it.
 */
const REFUSAL_CLASSES: ReadonlySet<string> = new Set([
  'validation',
  'permission',
  'not_found',
  'conflict',
  'limit',
]);

function readRefusal(body: { error?: unknown } | null): { sentence: string; denied: boolean } | null {
  const err = body?.error;
  if (err === null || typeof err !== 'object') return null;
  const env = err as { code?: unknown; class?: unknown; message?: unknown; details?: unknown };
  const cls = typeof env.class === 'string' ? env.class : '';
  // An envelope with no class, or a class this host does not know, is not something to read a
  // sentence out of — it may not be a kernel refusal at all.
  if (!REFUSAL_CLASSES.has(cls)) return null;
  const details = env.details as Record<string, unknown> | undefined;
  const candidate = [details?.['reason'], env.message].find(
    (v): v is string => typeof v === 'string' && v.trim() !== '',
  );
  if (candidate === undefined) return null;
  return { sentence: candidate, denied: cls === 'permission' || cls === 'not_found' };
}

export function pageNotice(title: string, body: string, kind: 'alert' | 'status' = 'alert'): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = `sa-page-notice sa-page-notice--${kind}`;
  wrap.dataset['notice'] = kind;
  const h = document.createElement('p');
  h.className = 'sa-page-notice__title';
  h.textContent = title;
  const p = document.createElement('p');
  p.className = 'sa-page-notice__body';
  p.textContent = body;
  wrap.append(h, p);
  return wrap;
}

/**
 * Draw one page.
 *
 * The header is the page's own name. Everything below it comes from the page's sections; there is no
 * second description of this screen anywhere, which is the point — the page is the only thing that
 * says what the page holds.
 */
export function renderPageSurface(page: PageMeta, options: PageRenderOptions): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'sa-surface sa-surface--page';
  wrap.dataset['page'] = page.key;

  // CAOS-498 — an embedded page does not write the screen's heading; the page around it already
  // did. Marked on the element as well as branched on, so a stylesheet and a test can both tell an
  // embedded surface from a destination without re-deriving it.
  if (options.embedded) {
    wrap.classList.add('sa-surface--embedded');
    wrap.dataset['embedded'] = 'true';
  } else {
    const header = document.createElement('caos-page-header');
    if (options.appLabel) header.setAttribute('eyebrow', options.appLabel);
    header.setAttribute('heading', page.label);
    wrap.append(header);
  }

  const body = document.createElement('div');
  body.className = 'sa-sections';
  wrap.append(body);

  // A page says what feeds it, and three of the four answers are not a stack of sections at all.
  // This surface draws the fourth. Before this check, an intake page — the shipped "Report a Bug"
  // form, which declares three fields, a submit button and a write route — was told it "contains
  // nothing", because it declares no SECTIONS. That is a false statement about the page, produced by
  // the very mechanism this surface added to stop false statements, so it is the first thing checked.
  //
  // CAOS-744 — `controlPlane` no longer falls into that same "cannot draw yet" notice: it is now
  // fetched and drawn through the SAME closed vocabulary (stat tiles / a roster / key-value rows,
  // CAOS-247) every other placed component already draws through, reading the fetch route + the
  // section/item structure straight off THIS page (never a second, hand-coded description of it).
  // `intake` is unchanged — still out of this ticket's scope.
  if (page.kind === 'intake') {
    body.append(
      pageNotice(
        `“${page.label}” is a form, and this screen cannot draw a form yet`,
        `“${page.label}” collects entries and submits them; it does not hold blocks of content. ` +
          'It draws where it is meant to appear — this screen does not draw that kind of page yet, ' +
          'so nothing is missing from the page itself.',
      ),
    );
    return wrap;
  }
  if (page.kind === 'controlPlane') {
    const { done } = renderControlPlaneSurface(page, options, body);
    (wrap as unknown as Record<string, unknown>)[CONTENT_SETTLED] = done;
    return wrap;
  }
  // CAOS-1767 — a `list` page shows many records of one object through that object's own list view.
  // It is checked beside `controlPlane` and for the same reason: it is not a stack of sections
  // either, and before this kind existed the only way to author a roster of records was to point a
  // page at a control-plane ROUTE, which meant a kernel route had to be written before the screen
  // could exist at all.
  //
  // THE DRAWING IS NOT IN THIS FILE and cannot be: it is the list surface, which lives in
  // `content-source.ts` — and that file already imports this one, so importing it back would put the
  // two in a cycle. It arrives through {@link PageRenderOptions.listDraw}, which `renderPage`
  // (`content-source.ts`) is the one place that supplies.
  //
  // A CALLER THAT REACHES HERE WITHOUT IT GETS A SENTENCE, not a list and not a blank. It is the
  // same shape the `intake` branch above has always had, and for the same reason: before this kind
  // existed a list page fell into the "has not said what it contains" notice below, which is a false
  // statement about a page that declares a complete list. Saying "this screen does not draw a list"
  // is true, searchable, and points at the one thing to change.
  if (page.kind === 'list') {
    if (!options.listDraw) {
      body.append(
        pageNotice(
          `“${page.label}” is a list, and this screen cannot draw a list`,
          `“${page.label}” shows a list of records; it does not hold blocks of content. ` +
            'It draws where it is meant to appear — this screen does not draw that kind of page, ' +
            'so nothing is missing from the page itself.',
        ),
      );
      return wrap;
    }
    const done = options.listDraw(page, options).then(
      (node) => {
        body.append(node);
      },
      () => {
        body.append(
          pageNotice(
            `Couldn’t load “${page.label}”`,
            `“${page.label}” shows a list of records, and that list could not be drawn. ` +
              'Check your connection and try again.',
          ),
        );
      },
    );
    (wrap as unknown as Record<string, unknown>)[CONTENT_SETTLED] = done;
    return wrap;
  }

  // CAOS-388 — the page declares things this surface does not draw. Said out loud, because a record
  // page opened through a tab silently losing its related lists is the same defect this ticket
  // exists to close, one level up.
  if (page.undrawn.length > 0) {
    body.append(
      pageNotice(
        `Some of “${page.label}” is not drawn on this screen`,
        `This page also declares ${listPhrase(page.undrawn)}, which this screen does not draw. ` +
          'They are still on the page; they appear on the record screen it was written for.',
      ),
    );
  }

  // ABSENT and EMPTY are two different things and get two different sentences. A page that has not
  // declared sections has an unfinished manifest; a page that declared none is finished and holds
  // nothing. Telling somebody the wrong one of those sends them to the wrong place to fix it.
  if (page.sections === undefined) {
    body.append(
      pageNotice(
        'This page has not said what it contains',
        `“${page.label}” does not list any blocks yet, so there is nothing to draw. Add a block to it and the contents will appear here.`,
        'status',
      ),
    );
    return wrap;
  }
  if (page.sections.length === 0) {
    body.append(
      pageNotice(
        'This page is empty',
        `“${page.label}” says it contains nothing. Place a field or a piece on it and it will appear here.`,
        'status',
      ),
    );
    return wrap;
  }

  const object = page.object ? options.meta.objects.get(page.object) : undefined;
  placeSectionsInRegions(body, page, object, options);
  return wrap;
}

/**
 * CAOS-744 — draw a `controlPlane`-kind page: fetch its declared READ route and draw the payload
 * through the page's OWN authored sections/items — the same closed vocabulary (stat tiles, a roster,
 * key-value rows, CAOS-247) an object-kind page's components already draw through, never a second,
 * hand-coded description of the same screen (the gap this ticket closes — see `page.ts`'s
 * `PageDataSourceSchema` doc and this file's own module doc for the fuller history).
 *
 * ── How a placed item asks for LIVE data (the authoring convention) ──────────────────────────────
 * A controlPlane page has no object binding, so a placed FIELD item is still meaningless here (as it
 * always was — {@link renderFieldItem} already says so). Live data is asked for on a placed
 * COMPONENT's `props`, by TWO conventions, both pure JSON the author writes and neither ever
 * containing a fetched value at authoring time:
 *
 *   • `props.valueAt` (a string, a JSON path into the fetched payload) — a SINGLE-VALUE binding. The
 *     resolved value is passed to the element as `value`, `valueAt` itself is dropped (it names WHERE
 *     to look, not a real input any component understands). This is what a stat tile or a
 *     `caos-key-value-row` placed on a controlPlane page uses.
 *   • `props.columns` (an array of `{ key, label }`) marks a component as THIS PAGE'S ROSTER: its rows
 *     come from `page.controlPlaneRowsAt` in the same payload, and its per-row actions are the row-
 *     scoped entries among `item.rowActions` ∪ `page.rowActions` (filtered to the ones naming this
 *     item via `rowActionsTarget`, or all of them when the page names none). One roster per page, the
 *     same limit CAOS-247's original renderer already had — see the module doc for why that is a
 *     scoping decision, not an oversight.
 *
 * ── THE SECURITY BOUNDARY — why this can never show "someone else's" data (CAOS-744, item 3) ──────
 * A controlPlane page's read is a bare `{ noun, verb }` ({@link RouteVerbRefMeta}). A page's own
 * `valueAt` only ever names a PATH INTO a payload the page already received, never a request
 * parameter, and a caller-scoped action ({@link isPageRowActionCallerScoped}) carries no `param` at
 * all. So the fetch this function issues is `/api/<noun>/<verb>`, plus at most:
 *
 *   • for a ROW action — one path segment read from the ROW the server already returned;
 *   • for a PLACED piece's `read` or `write` (CAOS-402 / CAOS-1575) — values bound from a CLOSED set
 *     of sources: the record the surface is bound to, a field of a payload the server already
 *     returned, the value the piece itself holds, or a NAMED PARAMETER OF THE ROUTE THIS SURFACE WAS
 *     OPENED AT.
 *
 * ── What CAOS-1575 changed about this claim, stated rather than glossed ──────────────────────────
 * This doc used to say that nothing here could construct a request naming a different principal
 * because nothing here HELD one to construct it from. That sentence was true while every value on
 * the wire had come back from the server, and a route parameter is CALLER-SUPPLIED — the first such
 * value that did not. The strong form stopped being true, so it is not left standing here: a doc
 * asserting a property nothing checks any more is worse than no doc, because it is read as a reason
 * not to look.
 *
 * What survives is narrower and is what the adversarial proof now holds. A page names WHICH value to
 * forward and never a value: no author-written text reaches a request parameter or a request body
 * through this vocabulary, a binding cannot remap a name (a parameter takes the route parameter
 * SPELLED THE SAME, so a page cannot aim one at whatever the route happened to carry), a route
 * parameter the page did not declare reaches the wire nowhere, and an unknown source word is refused
 * at deploy rather than passed through. A forwarded parameter names the THING being addressed, and
 * the ROUTE — not this vocabulary — still decides whether this caller may have it.
 *
 * So "the caller's own data" was never a promise this renderer keeps; it is a promise the ROUTE
 * keeps, by deriving its scope from the authenticated session server-side (`auth whoami`, `auth
 * logout`, `sessions list`, `viewer-settings get`/`set` — see `kernel/src/api/viewer-settings-
 * routes.ts` for the fullest worked statement of this). That was already the load-bearing half, and
 * it is unchanged. This renderer's contribution is that it cannot NAME a principal or an org: it can
 * forward only what the router matched, under the name the router matched it as. See
 * `host/test/page-surface-control-plane.test.ts` for the adversarial proof, which is extended in the
 * same change that narrowed this claim.
 */
function renderControlPlaneSurface(
  page: PageMeta,
  options: PageRenderOptions,
  body: HTMLElement,
): { done: Promise<void> } {
  const doFetch = options.controlPlaneFetch ?? defaultControlPlaneFetch;
  const read = page.controlPlaneRead;

  if (!read) {
    body.append(
      pageNotice(
        `“${page.label}” has nothing to read from`,
        `“${page.label}” is fed by a live feed, but does not name the route to read it from. ` +
          'Add a read route to the page, or change what feeds it.',
      ),
    );
    return { done: Promise.resolve() };
  }

  const actionsRow = document.createElement('div');
  actionsRow.className = 'sa-page-actions';
  body.append(actionsRow);

  /**
   * CAOS-1892 — where an action says what it did, and the ONE host on this surface that a re-read
   * does not clear.
   *
   * It has to be its own element. `statusHost` and `sectionsHost` are both emptied by `drawSections`
   * on every successful read, and an action's last act is to trigger exactly that read — so an
   * outcome written into either would be erased by the refresh that proves it worked. It is also
   * where the confirm/collect panel is drawn, which is why it sits ABOVE the status line: a form
   * that appears below a roster of twelve rows appears off screen.
   */
  const reportHost = document.createElement('div');
  reportHost.className = 'sa-page-action-report';
  /**
   * Announced, because "it reports what happened" has to be true for somebody who cannot see the
   * notice appear. `polite` rather than `assertive`: the outcome is worth saying and is not worth
   * cutting off the sentence being read, and the three states it carries — running, done, refused —
   * each replace the last, so a reader is never told about an act twice.
   */
  reportHost.setAttribute('aria-live', 'polite');
  const statusHost = document.createElement('div');
  const sectionsHost = document.createElement('div');
  sectionsHost.className = 'sa-sections';
  body.append(reportHost, statusHost, sectionsHost);

  statusHost.append(pageNotice('Loading…', `Loading “${page.label.toLowerCase()}”…`, 'status'));

  // The CALLER-scoped actions (no row) render ONCE, in the page's own header — they never depend on
  // the fetched payload, since they have no row to read a predicate against.
  const callerScoped = (page.rowActions ?? []).filter(isPageRowActionCallerScoped);
  for (const action of callerScoped) {
    actionsRow.append(renderCallerScopedActionButton(action, doFetch, reportHost, () => void reload()));
  }
  if (callerScoped.length === 0) actionsRow.remove();

  const reload = async (): Promise<void> => {
    try {
      const res = await doFetch(`/api/${read.noun}/${read.verb}`);
      const body = await res.json();
      /**
       * CAOS-1857 — the HTTP status is NOT the branch, and that is the correction. This read
       * `throw`ew on `!res.ok` before the body was ever inspected, so a route answering 403 with a
       * fully-formed refusal went straight to the connectivity notice while the identical refusal
       * sent with 200 reached the envelope check below. One of those two paths explained itself and
       * the other did not, for the same answer — and the status a refusal arrives with is not the
       * page's business.
       *
       * So both paths go through the same reading: a readable refusal is drawn, and only a body with
       * nothing to say falls through to the connectivity guess.
       */
      const refusalForStatus = res.ok ? null : readRefusal(body as { error?: unknown } | null);
      if (!res.ok && !refusalForStatus) throw new Error('control-plane read refused');
      // CAOS-788 — every kernel route answers `{ ok: true, data, meta }` (api/substrate.ts). The page's
      // authored paths (`rowsAt`, an item's `valueAt`) name fields of `data`, so they are read there.
      // A body that is not a success envelope is a failed read, never an empty page.
      const envelope = body as { ok?: unknown; data?: unknown; error?: unknown } | null;
      if (refusalForStatus || envelope === null || typeof envelope !== 'object' || envelope.ok !== true) {
        /**
         * CAOS-1857 — A REFUSAL SAYS WHAT IT REFUSED, and this is the one branch that used to make
         * that impossible. Every failure here, including a route answering a perfectly well-formed
         * refusal, fell through to the catch below and drew "Check your connection and try again."
         *
         * That sentence is wrong twice over for a refusal: the connection is fine, and trying again
         * cannot help. The case that found it is `account/orgs` for an org admin who is not the
         * account owner — a customer who would be told to check their network, forever, about a
         * decision that had been made correctly and explained in the response they were handed.
         *
         * So a refusal that carries a readable reason is drawn with it. Anything that does NOT —
         * a transport failure, a body that is not an envelope, a refusal with no reason — still
         * goes to the connectivity notice, because for those it is the honest guess.
         */
        const refusal = refusalForStatus ?? readRefusal(envelope);
        if (refusal) {
          statusHost.replaceChildren(
            pageNotice(
              refusal.denied ? `“${page.label}” is not available to you` : `Couldn't load “${page.label}”`,
              refusal.sentence,
            ),
          );
          sectionsHost.replaceChildren();
          return;
        }
        throw new Error('control-plane read did not succeed');
      }
      drawSections(envelope.data);
    } catch {
      statusHost.replaceChildren(
        pageNotice(
          `Couldn't load “${page.label}”`,
          'Check your connection and try again.',
        ),
      );
      sectionsHost.replaceChildren();
    }
  };

  const drawSections = (payload: unknown): void => {
    statusHost.replaceChildren();
    sectionsHost.replaceChildren();
    if (page.sections === undefined) {
      sectionsHost.append(
        pageNotice(
          'This page has not said what it contains',
          `“${page.label}” does not list any blocks yet, so there is nothing to draw. Add a block to it and the contents will appear here.`,
          'status',
        ),
      );
      return;
    }
    if (page.sections.length === 0) {
      sectionsHost.append(
        pageNotice(
          'This page is empty',
          `“${page.label}” says it contains nothing. Place a piece on it and it will appear here.`,
          'status',
        ),
      );
      return;
    }
    for (const section of orderSectionsByRegion(page, options.meta)) {
      sectionsHost.append(
        renderControlPlaneSection(page, section, payload, options, doFetch, () => void reload(), reportHost),
      );
    }
  };

  return { done: reload() };
}

/** True when a {@link PageRowActionMeta} carries NEITHER `param` nor `paramFrom` — a page-level,
 *  caller-scoped write with no row to key against. Mirrors the kernel's `isCallerScopedAction`
 *  (kernel/src/meta/page.ts); re-derived here rather than imported because the host reads its OWN
 *  wire-projected type ({@link PageRowActionMeta}), not the kernel's Zod-inferred one — see
 *  `metadata-source.ts`'s existing convention (e.g. `ListViewPresentation`) for why. */
function isPageRowActionCallerScoped(
  action: PageRowActionMeta,
): action is PageRowActionMeta & { readonly verb: RouteVerbRefMeta } {
  // CAOS-1616 — `verb !== undefined` FIRST, and it is the whole correction. An opening action
  // carries no `param` either, so the two-clause test this used to be answered "yes, caller-scoped"
  // for it and the header bar would have POSTed `/api/undefined/undefined`. Caller-scoped is a
  // statement about a WRITE with no row to key it to; an action that writes nothing is not one.
  return action.verb !== undefined && action.param === undefined && action.paramFrom === undefined;
}

/** True when a row action OPENS a record rather than writing one (CAOS-1616). */
function isPageRowActionOpening(
  action: PageRowActionMeta,
): action is PageRowActionMeta & { readonly destination: ActionDestinationMeta } {
  return action.destination !== undefined;
}

/**
 * CAOS-1892 — one drawn control for a collected value, with the one function that reads it back.
 *
 * `read` returns the value AS THE ROUTE SHOULD RECEIVE IT — a number for `number`, a boolean for
 * `boolean`, a string for the rest — and `undefined` for "nothing was supplied". That distinction is
 * the whole reason this is a function rather than a field read at submit time: an optional value
 * left blank must be ABSENT from the body, not sent as `""`. `account create` reads an absent
 * `label` as "no display name was given" and would read `""` as a display name that happens to be
 * the empty string, and those are different orgs.
 */
interface CollectedControlHandle {
  readonly el: HTMLElement;
  readonly read: () => string | number | boolean | undefined;
  /** False when this host cannot draw the authored control, so submit must refuse rather than send. */
  readonly drawable: boolean;
  readonly focus: () => void;
}

/**
 * CAOS-1892 — draw the control one collected value is edited with.
 *
 * A control this build does not know, and a picklist with nothing to choose, are both drawn as a
 * DISABLED control that explains itself rather than as nothing. That is CAOS-1616's decision for a
 * row action whose address will not resolve, applied here for the same reason: a control that
 * vanishes is indistinguishable from an action that never asked for the value, so the person
 * concludes the form is complete and meets a refusal from the route instead.
 */
function buildCollectedControl(field: PageCollectedFieldMeta): CollectedControlHandle {
  const unsupported = (why: string): CollectedControlHandle => {
    const el = document.createElement('input');
    el.type = 'text';
    el.className = 'sa-collect__control sa-collect__control--unsupported';
    el.disabled = true;
    el.setAttribute('aria-disabled', 'true');
    el.title = why;
    el.placeholder = why;
    return { el, read: () => undefined, drawable: false, focus: () => el.focus() };
  };

  if (field.control === 'picklist') {
    if (!field.options || field.options.length === 0) {
      return unsupported('This screen was given no options to choose from, so this cannot be answered.');
    }
    const select = document.createElement('select');
    select.className = 'sa-collect__control';
    // A leading EMPTY option, always, so the drawn state of an unanswered picklist is "nothing
    // chosen" rather than silently "the first one". Without it a required chooser is satisfied
    // before anybody has looked at it, and the value sent is whichever option happened to be
    // authored first — a decision nobody made.
    const blank = document.createElement('option');
    blank.value = '';
    blank.textContent = field.placeholder ?? 'Choose…';
    select.append(blank);
    for (const option of field.options) {
      const o = document.createElement('option');
      o.value = option.value;
      o.textContent = option.label;
      select.append(o);
    }
    return {
      el: select,
      read: () => (select.value === '' ? undefined : select.value),
      drawable: true,
      focus: () => select.focus(),
    };
  }

  if (field.control === 'boolean') {
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.className = 'sa-collect__control sa-collect__control--boolean';
    // Drawn unticked and READ ALWAYS: a checkbox has no empty state, so it is never "not supplied"
    // and `false` is as much an answer as `true`. This carries no authored default — the schema
    // offers none — so an untouched box sends `false`, which is what it shows.
    return { el: box, read: () => box.checked, drawable: true, focus: () => box.focus() };
  }

  if (field.control === 'textarea') {
    const area = document.createElement('textarea');
    area.className = 'sa-collect__control';
    area.rows = 3;
    if (field.placeholder) area.placeholder = field.placeholder;
    return {
      el: area,
      read: () => (area.value.trim() === '' ? undefined : area.value),
      drawable: true,
      focus: () => area.focus(),
    };
  }

  if (field.control === 'text' || field.control === 'password' || field.control === 'number') {
    const input = document.createElement('input');
    // `password` is its own authored word and this is the only place it can be honoured. Deriving
    // masking from the field's NAME (a path ending in "password") would be a guess that is wrong in
    // both directions — it would mask an unrelated field called `passwordPolicy` and leave
    // `admin.secret` in clear.
    input.type = field.control === 'password' ? 'password' : field.control === 'number' ? 'number' : 'text';
    input.className = 'sa-collect__control';
    if (field.control === 'password') input.autocomplete = 'new-password';
    if (field.placeholder) input.placeholder = field.placeholder;
    return {
      el: input,
      read: () => {
        const raw = input.value.trim();
        if (raw === '') return undefined;
        if (field.control !== 'number') return input.value;
        const n = Number(raw);
        // NOT sent as a string when it will not parse. The route would refuse "twelve" as a type
        // error about a field the person can see and fix, but only after a round trip; `undefined`
        // here means the required check below names it as unanswered, which is where they are.
        return Number.isFinite(n) ? n : undefined;
      },
      drawable: true,
      focus: () => input.focus(),
    };
  }

  return unsupported(`This screen cannot draw a “${field.control}” control, so this cannot be answered.`);
}

/**
 * CAOS-1892 — the panel an action shows before it runs: the warning it must state, the values it
 * must ask for, or both.
 *
 * It is drawn INTO THE PAGE rather than as a browser `confirm()` dialog, for two reasons that are
 * not style. A native dialog cannot carry a form, so collecting and warning would need two
 * different mechanisms for one press; and `window.confirm` is unavailable in the environment the
 * surface tests run in, which would make the one branch that must never regress the one branch
 * that cannot be tested.
 */
function buildActionRequestPanel(
  action: PageRowActionMeta & { readonly verb: RouteVerbRefMeta },
  /** The action's authored constant, which the collected values are written ON TOP of. */
  fixedBody: Readonly<Record<string, string | number | boolean | null>> | undefined,
  proceed: (body: Record<string, unknown>) => void,
  cancel: () => void,
): HTMLElement {
  const panel = document.createElement('form');
  panel.className = 'sa-collect';
  panel.dataset['actionRequest'] = action.id;

  const heading = document.createElement('p');
  heading.className = 'sa-collect__heading';
  heading.textContent = action.label;
  panel.append(heading);

  if (action.confirm) {
    // The author's own sentence, verbatim. It is the only thing on this panel that knows what the
    // act costs, and the host has nothing truer to replace it with.
    const warning = pageNotice('Before this runs', action.confirm.message, 'alert');
    warning.dataset['actionConfirm'] = action.id;
    panel.append(warning);
  }

  const problem = document.createElement('p');
  problem.className = 'sa-collect__problem';
  problem.dataset['collectProblem'] = action.id;
  // `alert` and not `status`: this appears because a press was refused, and it names the one thing
  // standing between the person and the act they just asked for. It is worth interrupting for.
  problem.setAttribute('role', 'alert');
  problem.hidden = true;

  const handles: Array<{ field: PageCollectedFieldMeta; handle: CollectedControlHandle }> = [];
  for (const field of action.collect ?? []) {
    const handle = buildCollectedControl(field);
    handles.push({ field, handle });

    const group = document.createElement('div');
    group.className = 'sa-collect__field';
    const label = document.createElement('label');
    label.className = 'sa-collect__label';
    const labelId = `${action.id}-${field.path.replace(/[^a-zA-Z0-9]+/g, '-')}`;
    handle.el.id = labelId;
    label.htmlFor = labelId;
    label.textContent = field.required ? `${field.label} (required)` : field.label;
    group.append(label, handle.el);
    panel.append(group);
  }

  panel.append(problem);

  const buttons = document.createElement('div');
  buttons.className = 'sa-collect__buttons';

  const go = document.createElement('button');
  go.type = 'submit';
  go.className = 'sa-page-action';
  go.dataset['collectSubmit'] = action.id;
  if (action.intent) go.dataset['intent'] = action.intent;
  go.textContent = action.confirm?.confirmLabel ?? action.label;

  const stop = document.createElement('button');
  stop.type = 'button';
  // The same class the proceeding button and every shipped action button carries. No quieter
  // variant: nothing styles `.sa-page-action` today, so a modifier would be a class with no rule
  // implying a look it does not have.
  stop.className = 'sa-page-action';
  stop.dataset['collectCancel'] = action.id;
  stop.textContent = 'Cancel';
  stop.addEventListener('click', cancel);

  buttons.append(go, stop);
  panel.append(buttons);

  panel.addEventListener('submit', (event) => {
    event.preventDefault();
    problem.hidden = true;

    // Null prototype, and the author's constant copied in FIRST so a collected value lands on top
    // of it. `validateCollectedActionValues` refuses that overlap at deploy, so this order only
    // decides the outcome for metadata deployed before the gate existed — and the person's own
    // typing is the better of the two answers to show them, because it is the one they can see.
    const body: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
    for (const [key, value] of Object.entries(fixedBody ?? {})) body[key] = value;
    for (const { field, handle } of handles) {
      if (!handle.drawable) {
        // Refused as a whole rather than sent short. A body missing a field the action declared is
        // a request the author never described, and the route's refusal would name its own missing
        // argument rather than the screen that could not ask for it.
        problem.textContent = `“${field.label}” cannot be answered on this screen, so nothing was sent.`;
        problem.hidden = false;
        handle.focus();
        return;
      }
      // Re-checked at the moment of sending as well as at deploy, because this host may be drawing
      // metadata that was deployed before `validateCollectedActionValues` existed.
      if (field.path.split('.').some((seg) => RESERVED_WRITE_PATH_SEGMENTS.has(seg.toLowerCase()))) {
        problem.textContent = 'This screen is misconfigured and nothing was sent.';
        problem.hidden = false;
        return;
      }
      const value = handle.read();
      if (value === undefined) {
        if (field.required) {
          problem.textContent = `“${field.label}” is needed before this can run.`;
          problem.hidden = false;
          handle.focus();
          return;
        }
        continue; // optional and blank ⇒ absent from the body, not sent empty
      }
      writeJsonPath(body, field.path, value);
    }
    proceed(body);
  });

  return panel;
}

/**
 * CAOS-1892 — send one page action and SAY WHAT HAPPENED.
 *
 * ── The defect this replaces ─────────────────────────────────────────────────────────────────────
 * Both action paths used to be `try { await doFetch(...) } finally { onDone() }`, with the response
 * never read. A refused Suspend and a successful one were indistinguishable: the button re-enabled,
 * the page re-read, and the row either had changed or had not. Nothing was wrong on screen, which
 * is the worst available outcome — a person cannot tell a failure from a success they misread, and
 * the only way to find out was to re-read the roster and infer it.
 *
 * ── Why the HTTP status is not the branch ────────────────────────────────────────────────────────
 * The same correction CAOS-1857 made to this page's READ applies here: a kernel route may answer a
 * fully-formed refusal with 200 and an `{ error }` envelope, and may answer one with 4xx. Branching
 * on `res.ok` alone would explain one and not the other for the same answer. So the envelope is
 * read first, through the same {@link readRefusal} the read uses, and the route's own sentence is
 * shown verbatim — a refusal reading "could not save" is indistinguishable from a network failure,
 * and the two call for opposite responses.
 *
 * ── Why a success is announced at all ───────────────────────────────────────────────────────────
 * `onDone()` re-reads the page, so a change is visible — but only to somebody who knows what the
 * roster looked like a moment ago. For a CREATE there is no before-and-after to compare: a new row
 * among twelve is not self-announcing. AC 4 asks for the action to report what happened rather than
 * leaving the person to refresh and work it out, and that includes the case where it worked.
 */
async function sendPageAction(
  action: PageRowActionMeta & { readonly verb: RouteVerbRefMeta },
  url: string,
  body: Record<string, unknown> | undefined,
  doFetch: NonNullable<PageRenderOptions['controlPlaneFetch']>,
  reportHost: HTMLElement,
  onDone: () => void,
  /** The control that was pressed, held disabled for as long as the request is in flight. */
  pressed?: HTMLElement,
): Promise<void> {
  const route = `${action.verb.noun} ${action.verb.verb}`;
  const report = (node: HTMLElement): void => {
    node.dataset['actionReport'] = action.id;
    reportHost.replaceChildren(node);
  };
  /**
   * Disabled for the duration, and RE-ENABLED only when the outcome is known.
   *
   * Both action paths did this before and it is not decoration: `account create` provisions an org,
   * and a second press while the first is in flight is a second org. The old row-action path set
   * `aria-disabled` and never cleared it, which was survivable only because the whole row was
   * rebuilt by the re-read that followed; a refused action now leaves the row in place, so the
   * control has to come back.
   */
  pressed?.setAttribute('aria-disabled', 'true');
  if (pressed instanceof HTMLButtonElement) pressed.disabled = true;
  const release = (): void => {
    pressed?.removeAttribute('aria-disabled');
    if (pressed instanceof HTMLButtonElement) pressed.disabled = false;
  };

  // The third state AC 4 names, and it is drawn BEFORE the await rather than after it. A create
  // provisions an org and is not instant; without this the screen is silent for the whole of it and
  // the only honest reading of a silent screen is that the press did nothing.
  report(pageNotice(`${action.label} is running…`, `Asking “${route}”. This can take a moment — nothing else is needed.`, 'status'));

  try {
    const res = await doFetch(url, {
      method: 'POST',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const answered = (await res.json().catch(() => undefined)) as { error?: unknown } | undefined;
    const refusal = readRefusal(answered ?? null);
    if (refusal) {
      release();
      report(pageNotice(`${action.label} did not run`, refusal.sentence));
      return;
    }
    if (!res.ok) {
      // Refused, and the answer had nothing readable in it. Said as exactly that rather than as a
      // guess about connectivity, which is a different fault with a different fix.
      release();
      report(pageNotice(`${action.label} did not run`, `“${route}” refused it and did not say why.`));
      return;
    }
    release();
    report(pageNotice(`${action.label} is done`, `“${route}” accepted it. The page below has been re-read.`, 'status'));
    onDone();
  } catch {
    release();
    report(pageNotice(`${action.label} did not run`, `“${route}” could not be reached. Nothing was changed.`));
  }
}

/**
 * CAOS-1892 — what pressing an action does: ask first if it must, then send.
 *
 * An action with neither a confirmation nor collected values is sent on the press, which is every
 * action authored before this existed and is why they are unchanged by it.
 */
function pressPageAction(
  action: PageRowActionMeta & { readonly verb: RouteVerbRefMeta },
  url: string,
  doFetch: NonNullable<PageRenderOptions['controlPlaneFetch']>,
  reportHost: HTMLElement,
  onDone: () => void,
  fixedBody?: Readonly<Record<string, string | number | boolean | null>>,
  /** The control that was pressed, so it can be held disabled while the request is in flight. */
  pressed?: HTMLElement,
): void {
  const asksSomething = (action.collect && action.collect.length > 0) || action.confirm !== undefined;
  if (!asksSomething) {
    void sendPageAction(action, url, fixedBody ? { ...fixedBody } : undefined, doFetch, reportHost, onDone, pressed);
    return;
  }
  const panel = buildActionRequestPanel(
    action,
    fixedBody,
    (body) => {
      // The PANEL's own submit button, not the control that opened the panel: by this point the
      // person is pressing the one inside the form, and that is the one that must not be pressed
      // twice. The opening control is left alone — the panel has replaced it as the live thing.
      void sendPageAction(
        action,
        url,
        body,
        doFetch,
        reportHost,
        onDone,
        panel.querySelector<HTMLElement>('[data-collect-submit]') ?? undefined,
      );
    },
    () => {
      // Declining leaves the request unsent AND says so. Clearing the panel to nothing would be
      // indistinguishable from the panel having failed to open.
      const cancelled = pageNotice(`${action.label} was not run`, 'Nothing was sent.', 'status');
      cancelled.dataset['actionReport'] = action.id;
      reportHost.replaceChildren(cancelled);
    },
  );
  reportHost.replaceChildren(panel);
  // Put the person where the work is. A panel that opens above the fold of a long roster, with the
  // press that opened it now scrolled past, reads as nothing having happened.
  panel.querySelector<HTMLElement>('.sa-collect__control:not([disabled])')?.focus();
}

/**
 * Build a standalone, CALLER-scoped action button (CAOS-744 AC: "a non-record page can declare at
 * least one write action... that POSTs a control-plane route with no row to scope it to"). POSTs
 * `/api/<noun>/<verb>` — NOTHING appended, no body — then calls `onDone` (a re-read of the page's own
 * controlPlane payload) so a change the action made is reflected without a full reload.
 *
 * CAOS-1892 — it may now ask for values or state a warning first, and it reports what happened
 * either way. The POST itself is unchanged.
 */
function renderCallerScopedActionButton(
  // CAOS-1616 — the WRITE arm only. Narrowed from `PageRowActionMeta` when that type became a
  // union: this function dereferences `action.verb`, and an opening action has none. Stating the
  // arm in the signature is what makes `isPageRowActionCallerScoped`'s `verb !== undefined` check
  // load-bearing rather than decorative — drop it and this stops compiling, instead of compiling
  // and POSTing `/api/undefined/undefined`.
  action: PageRowActionMeta & { readonly verb: RouteVerbRefMeta },
  doFetch: NonNullable<PageRenderOptions['controlPlaneFetch']>,
  reportHost: HTMLElement,
  onDone: () => void,
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'sa-page-action';
  btn.dataset['actionId'] = action.id;
  if (action.intent) btn.dataset['intent'] = action.intent;
  btn.textContent = action.label;
  btn.addEventListener('click', () => {
    pressPageAction(
      action,
      `/api/${action.verb.noun}/${action.verb.verb}`,
      doFetch,
      reportHost,
      onDone,
      action.body,
      btn,
    );
  });
  return btn;
}

/** Draw one titled block of a controlPlane page — the SAME `caos-detail-section` shape an
 *  object-kind page's block uses, its items resolved against the LIVE payload instead of a record. */
function renderControlPlaneSection(
  page: PageMeta,
  section: PageSectionMeta,
  payload: unknown,
  options: PageRenderOptions,
  doFetch: NonNullable<PageRenderOptions['controlPlaneFetch']>,
  onActionDone: () => void,
  /** CAOS-1892 — where a row action's outcome is written; outlives the re-read this section does not. */
  reportHost: HTMLElement,
): HTMLElement {
  const sec = document.createElement('caos-detail-section');
  if (section.label) sec.setAttribute('heading', section.label);
  sec.dataset['section'] = section.id;

  const grid = document.createElement('div');
  grid.className = 'sa-field-grid';
  grid.style.setProperty('--sa-cols', String(section.columns ?? 2));

  if (section.items === undefined) {
    grid.append(pageNotice('This block has not said what it holds', 'Nothing is listed in it yet, so there is nothing to draw here.', 'status'));
  } else if (section.items.length === 0) {
    grid.append(pageNotice('Nothing is placed here yet', 'This block is empty.', 'status'));
  } else {
    for (const item of section.items) {
      grid.append(
        item.kind === 'field'
          ? renderFieldItem(page, item, undefined, options)
          : item.kind === 'component'
            ? renderControlPlaneComponentItem(page, item, payload, options, doFetch, onActionDone, reportHost)
            : item.kind === 'embed'
              ? renderEmbedItem(item, options)
              : renderUnknownItem(item),
      );
    }
  }

  sec.append(grid);
  return sec;
}

/**
 * Draw one placed component against the LIVE controlPlane payload. Three shapes, told apart by the
 * component's own authored `props` (see {@link renderControlPlaneSurface}'s doc):
 *
 *   1. `props.columns` present — this IS the page's roster: rows come from `page.controlPlaneRowsAt`,
 *      row actions from `item.rowActions` ∪ the row-scoped entries of `page.rowActions`.
 *   2. `props.valueAt` present — a single value is resolved from the payload and passed as `value`.
 *   3. neither — the component draws with its authored (static) props unchanged, exactly as an
 *      object-kind page's component does; a controlPlane page may still place a purely decorative
 *      piece that needs no live data.
 */
function renderControlPlaneComponentItem(
  page: PageMeta,
  item: PageComponentItemMeta,
  payload: unknown,
  options: PageRenderOptions,
  doFetch: NonNullable<PageRenderOptions['controlPlaneFetch']>,
  onActionDone: () => void,
  reportHost: HTMLElement,
): HTMLElement {
  const columns = item.props?.['columns'];
  if (Array.isArray(columns)) {
    return renderControlPlaneRoster(page, item, columns, payload, options, doFetch, onActionDone, reportHost);
  }

  const valueAt = item.props?.['valueAt'];
  if (typeof valueAt === 'string' && valueAt.length > 0) {
    const resolved = readJsonPath(payload, valueAt);
    const { valueAt: _valueAt, ...rest } = item.props ?? {};
    void _valueAt;
    const liveProps = { ...rest, value: resolved === undefined || resolved === null ? undefined : resolved };
    return renderComponentItem(page, { ...item, props: liveProps }, options);
  }

  // CAOS-425 - the same idea for a piece fed a STRUCTURE rather than a scalar. `valueAt` names
  // the path to one value; `dataAt` names the path to the whole shape a piece's declared `json`
  // input takes. Without it a component could declare such an input, be offered for a placement,
  // and still be unfeedable from any page anyone could author - built, placed, and drawing
  // nothing. Absent leaves every existing placement exactly as it was.
  const dataAt = item.props?.['dataAt'];
  if (typeof dataAt === 'string' && dataAt.length > 0) {
    const resolved = readJsonPath(payload, dataAt);
    const { dataAt: _dataAt, ...rest } = item.props ?? {};
    void _dataAt;
    // Only a MISSING path is "not yet". `readJsonPath` returns `undefined` when the payload does
    // not carry the path at all, and that is the one case worth leaving the input unset for: the
    // piece's own contract says an unset input draws its loading state.
    //
    // A null that was actually RETURNED is an answer, and passing it through as one matters here
    // rather than in the abstract: `data/home` returns `pipeline: null` to mean "nothing in this
    // workspace keeps a lifecycle to break down". Collapsing that into "unset" would leave a piece
    // spinning forever on a question that has already been answered.
    const liveProps = resolved === undefined ? { ...rest } : { ...rest, data: resolved };
    const el = renderComponentItem(page, { ...item, props: liveProps }, options);
    // `applyProps` drops null along with undefined, and deliberately so: everywhere else a null
    // is something a PAGE AUTHOR wrote in static props, where it means "leave this unset". Here
    // it is something a READ returned, which is the opposite statement, so it is set directly
    // rather than routed through a rule written for the other case. Without this the branch
    // above is inert - the piece cannot tell an answered null from a path that never arrived.
    if (resolved === null) (el as unknown as Record<string, unknown>)['data'] = null;
    return el;
  }

  return renderComponentItem(page, item, options);
}

/** A roster row's field values, read generically off whatever the control-plane payload returned. */
type ControlPlaneRow = Record<string, unknown>;

/** Extract the row array from the payload at `rowsAt` (or the payload itself), object rows only. */
function extractControlPlaneRows(payload: unknown, rowsAt?: string): ControlPlaneRow[] {
  const raw = rowsAt ? readJsonPath(payload, rowsAt) : payload;
  return Array.isArray(raw) ? raw.filter((r): r is ControlPlaneRow => r != null && typeof r === 'object') : [];
}

/** Draw the page's ONE roster component: a `caos-data-table` fed columns + rows AS DATA, plus a
 *  per-row action column when any row-scoped action applies. */
function renderControlPlaneRoster(
  page: PageMeta,
  item: PageComponentItemMeta,
  columns: unknown[],
  payload: unknown,
  options: PageRenderOptions,
  doFetch: NonNullable<PageRenderOptions['controlPlaneFetch']>,
  onActionDone: () => void,
  reportHost: HTMLElement,
): HTMLElement {
  const installed = options.meta.components.find((c) => c.key === item.key);
  if (!installed) {
    return pageNotice(
      `“${item.key}” is not installed here`,
      `This page places a piece called “${item.key}”, and this workspace does not have it. Install the package that provides it, or take the piece off the page.`,
    );
  }

  const dataColumns = columns
    .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
    .filter((c): c is { key: string; label?: string } => typeof c['key'] === 'string')
    .map((c) => ({ key: c.key, label: c.label ?? c.key }));

  // A row action applies to THIS roster when the page names no target at all, or names THIS item.
  const target = page.rowActionsTarget;
  const rowActions = [...(item.rowActions ?? []), ...(page.rowActions ?? [])].filter(
    (a) => !isPageRowActionCallerScoped(a) && (target === undefined || target === item.id),
  );

  const rows = extractControlPlaneRows(payload, page.controlPlaneRowsAt);
  const hasActions = rowActions.length > 0;
  const actionCell = (row: ControlPlaneRow): HTMLElement =>
    renderRowActionCell(row, rowActions, doFetch, onActionDone, reportHost, options.objectHomes);

  // CAOS-1908 — a page that declares the reader's controls is drawn by the host's own collection
  // surface, which is where search, ordering, the board and the narrowing tabs already live. A page
  // that declares none takes the branch below, unchanged, and that split is deliberate: see
  // {@link PageMeta.rosterControls} for why absent cannot mean "draw a collection with defaults".
  if (page.rosterControls) {
    return renderControlPlaneCollection(
      page,
      item,
      page.rosterControls,
      dataColumns,
      rows,
      hasActions ? actionCell : undefined,
      options,
    );
  }

  const table = document.createElement(installed.tag);
  table.dataset['item'] = item.id;
  table.setAttribute('empty-label', `No ${page.label.toLowerCase()} yet`);
  (table as unknown as { columns: unknown }).columns = hasActions
    ? [...dataColumns, { key: '__actions', label: 'Actions', align: 'end' }]
    : dataColumns;
  (table as unknown as { rows: unknown }).rows = rows.map((row, i) => {
    const cells: Record<string, unknown> = {};
    for (const c of dataColumns) cells[c.key] = row[c.key] ?? null;
    if (hasActions) cells['__actions'] = { node: actionCell(row) };
    return { id: String(row['id'] ?? i), cells };
  });
  // CAOS-654 — a roster holds what its placement says it holds (a caption, an action bar), as every
  // other placed piece does, beside the rows it draws from the fetch.
  appendPlacedChildren(page, table, item, options);
  return table;
}

/** The text a roster cell reads as, for searching and for a row's stand-in name. */
function cellText(value: unknown): string {
  if (value == null) return '';
  return typeof value === 'string' ? value : String(value);
}

/**
 * CAOS-1908 — draw a `controlPlane` page's roster through {@link renderCollection}, so it carries
 * the search box, the ordering menu, the narrowing tabs and the board that a roster page already has.
 *
 * ── WHY THIS REUSES THE COLLECTION SURFACE RATHER THAN GROWING A TOOLBAR HERE ──────────────────
 * Every one of these behaviours is implemented once, in `collection-surface.ts`, and `roster-page.ts`
 * does nothing but configure it. A second toolbar beside it would be the thing that drifts — which is
 * the reason `buildFilterTabs` was exported in the first place (CAOS-544) rather than copied into the
 * roster detail page. This is its third consumer, and the tabs and the one-question-per-tab rule come
 * from the same functions the other two use.
 *
 * ── WHAT IS NOT SHARED, AND WHY ────────────────────────────────────────────────────────────────
 * A roster page's `filterTabValue` reads a column's `valueAt`/`cases` cascade to decide what a tab
 * interrogates. A control-plane roster has no such cascade: its columns are plain key/label pairs
 * over raw payload rows, so a tab's value is read off the row directly, by the tab's own `column` or
 * the roster's `filterField`. `rowMatchesFilter` itself IS reused, because it already takes the
 * filter and the value as two arguments and so does not care where the value came from.
 */
function renderControlPlaneCollection(
  page: PageMeta,
  item: PageComponentItemMeta,
  controls: NonNullable<PageMeta['rosterControls']>,
  dataColumns: readonly { key: string; label: string }[],
  rows: readonly ControlPlaneRow[],
  actionCell: ((row: ControlPlaneRow) => HTMLElement) | undefined,
  options: PageRenderOptions,
): HTMLElement {
  const root = document.createElement('div');
  root.dataset['item'] = item.id;

  // The name a row reads as. `CollectionRow.title` is required, and a control-plane roster declares
  // no title column, so an unset `titleField` falls back to the FIRST column rather than to an empty
  // string: a blank name in card and board readings looks like missing data, not like a page that
  // declined to name its rows.
  const titleKey = controls.titleField ?? dataColumns[0]?.key;

  /**
   * CAOS-1912 — which column carries the row's link, in table mode.
   *
   * The name column, resolved exactly as `titleKey` is, and for the same reason: that is where a
   * reader looks for what the row IS, so that is where they expect to click. `titleField` when the
   * page declares one and it is actually drawn, otherwise the first column.
   *
   * WHY THE PAGE DOES NOT MARK ITS OWN COLUMN. A roster page marks one, as `link: true` on a column
   * it declared. A setup page declares no columns of its own — they are an input to whichever
   * component it placed (`data_table`'s `columns`) — so there is nothing here to hang a `link` flag
   * on without inventing a second column vocabulary beside the component's, and a page would then
   * have to repeat its component's column list in order to annotate one entry of it. Deriving it is
   * the smaller statement: a page says WHERE a row goes and the surface decides what to make
   * clickable, which is the division every other key in `rosterControls` already follows.
   *
   * The `titleField`-is-drawn test matters. `titleField` may legitimately name a field that is not a
   * column — it is the card and board TITLE, which needs no column — and keying the link to it
   * unconditionally would write an anchor into a cell no column draws, so the table would show no
   * link at all while this code looked like it had made one.
   */
  const linkColumnKey =
    controls.titleField !== undefined && dataColumns.some((c) => c.key === controls.titleField)
      ? controls.titleField
      : dataColumns[0]?.key;

  let activeFilter = controls.filterDefault ?? 0;
  // A `filterDefault` past the end would leave every tab inactive and narrow by a tab that is not
  // there. The kernel refuses it on the parse path; this is the host refusing to act on one that
  // reached it anyway, rather than indexing off the end of the array.
  if (!controls.filters || activeFilter >= controls.filters.length) {
    activeFilter = 0;
  }

  const draw = (): void => {
    const narrowed =
      controls.filters && canNarrow(controls.filters, activeFilter, controls.filterField)
        ? rows.filter((r) => {
            const tab = controls.filters![activeFilter]!;
            const key = tab.column ?? controls.filterField;
            return rowMatchesFilter(tab, key === undefined ? undefined : r[key]);
          })
        : rows;

    const collectionRows = narrowed.map((row, i) => {
      const rowId = String(row['id'] ?? i);
      const filled = controls.detailRoute ? fillPath(controls.detailRoute, row) : undefined;
      const href = filled?.complete ? filled.path : undefined;

      const cells: Record<string, unknown> = {};
      for (const c of dataColumns) {
        // CAOS-1912 — the opening column is an ANCHOR, and in table mode it is the ONLY thing that
        // opens a row.
        //
        // This is not belt-and-braces beside `href` below; it is the half that works on the mode
        // these pages actually author. `CollectionRow.href` reaches the card grid and the board
        // (`cardsOf`, `boardOf` both read it), and reaches the TABLE through nothing at all:
        // `collection-surface.ts` builds its table rows as `{ id, cells }` and drops every other
        // member, and `CaosTableRow` in caos-ui declares no `href` and emits no open event. So a
        // roster with `defaultMode: 'table'` — which is what `feedback_review.json` declares — would
        // have validated, deployed, and left every row exactly as unclickable as before, with the
        // metadata and the renderer both looking correct. That is the failure this ticket is about,
        // reproduced one layer down.
        //
        // The cell `{ node }` form is how the shared surface already takes a built element, and the
        // anchor is the same shape `roster-page.ts` gives a column declared `link: true`.
        cells[c.key] = href !== undefined && c.key === linkColumnKey ? { node: rowLink(cellText(row[c.key]), href) } : (row[c.key] ?? null);
      }
      if (actionCell) cells[PAGE_ROSTER_ACTION_COLUMN] = { node: actionCell(row) };

      return {
        id: rowId,
        cells,
        // CAOS-1912 — where this row OPENS. `CollectionRow.href` has always existed and nothing was
        // supplying it here, which is why a setup page's roster could be searched and sorted and its
        // rows still went nowhere.
        //
        // Filled through `fillPath`, the SAME helper `roster-page.ts` fills its identically-spelled
        // `detailRoute` with. One vocabulary, one filler: any `{field}` resolves from the row, so
        // `/setup/objects/{object}/fields/{name}` works here exactly as it does there. A local
        // `replaceAll('{id}', …)` would have made this key a dialect that silently leaves every
        // other token in the address as literal text.
        //
        // `complete` is the guard, and it is `fillPath`'s existing contract rather than a new rule:
        // a token naming something the row does not carry yields a half-path, and half a path lands
        // somewhere arbitrary. The row is passed RAW and `rowId` is deliberately NOT spread in —
        // `rowId` falls back to the row INDEX for `CollectionRow.id`, and an address built from an
        // ordinal means a different record after the next read, which is worse than no link because
        // it works until it quietly does not. So a roster whose rows carry no `id` draws no links,
        // and the honest fix is to project one in the read.
        ...(href !== undefined ? { href } : {}),
        title: titleKey ? cellText(row[titleKey]) : '',
        ...(controls.stageField ? { stageValue: cellText(row[controls.stageField]) } : {}),
        // The search matches `searchText ?? title`, so the declared fields are folded into one
        // string here. Without this a search would match the title column alone — a box that draws,
        // accepts a term and finds nothing, which reads as an empty queue rather than a broken page.
        ...(controls.searchFields
          ? { searchText: controls.searchFields.map((f) => cellText(row[f])).join(' ') }
          : {}),
      };
    });

    const tabsBar =
      controls.filters && controls.filters.some((_, i) => canNarrow(controls.filters, i, controls.filterField))
        ? buildFilterTabs(controls.filters, activeFilter, (i) => {
            activeFilter = i;
            draw();
          })
        : undefined;

    const { root: surface } = renderCollection({
      key: `page.${page.key}.${item.id}`,
      heading: page.label,
      columns: [
        ...dataColumns.map((c) => ({ key: c.key, label: c.label })),
        ...(actionCell ? [{ key: PAGE_ROSTER_ACTION_COLUMN, label: 'Actions', align: 'end' as const }] : []),
      ],
      rows: collectionRows,
      ...(controls.searchPlaceholder !== undefined ? { searchPlaceholder: controls.searchPlaceholder } : {}),
      // No declared fields ⇒ no search box, rather than one that cannot match anything.
      ...(controls.searchFields === undefined ? { searchable: false } : {}),
      ...(controls.stages !== undefined ? { stages: controls.stages } : {}),
      ...(controls.sortOptions !== undefined ? { sortOptions: controls.sortOptions } : {}),
      ...(controls.defaultMode !== undefined ? { defaultMode: controls.defaultMode } : {}),
      ...(tabsBar !== undefined ? { filters: tabsBar } : {}),
      // CAOS-1912 — the card grid, the split view and the board all announce an opened row as an
      // EVENT rather than following an anchor, so `href` on the row is not enough for them on its
      // own: without a listener the card is a focusable control that does nothing, which is the
      // state `bindCardNavigation` was written to fix for a placed card grid and is the precedent
      // this follows. The map is built from the rows actually on screen, so a row the active tab
      // narrowed away cannot be opened by a stale id.
      ...(controls.detailRoute !== undefined
        ? {
            onOpen: (id: string): void => {
              const href = collectionRows.find((r) => r.id === id)?.href;
              if (href !== undefined) navigateFromFrame(href);
            },
          }
        : {}),
      emptyLabel: (s) =>
        s.search.trim() !== '' && controls.searchEmptyLabel !== undefined
          ? controls.searchEmptyLabel.replace('{search}', s.search.trim())
          : (controls.emptyLabel ?? `No ${page.label.toLowerCase()} yet`),
    });
    root.replaceChildren(surface);
    // CAOS-654 — whatever the placement itself holds (a caption, an action bar) still belongs with
    // the rows, exactly as it does on the bare-table branch.
    appendPlacedChildren(page, root, item, options);
  };

  draw();
  return root;
}

/**
 * CAOS-1912 — the anchor a roster row opens through, for the name cell of a table.
 *
 * A real `<a href>` and not a click handler, deliberately: that is what makes the destination
 * visible on hover, openable in a new tab, and copyable as a link, which is half of what "a
 * submission has an address" means in the ticket. The handler on top is the single-page path — the
 * modifier-key and non-primary-button tests are what keep ctrl-click and middle-click behaving as
 * the browser's, rather than being swallowed into an in-frame navigation.
 *
 * `navigateFromFrame` is the seam, the same one `bindCardNavigation` uses twenty lines below, which
 * falls back to a real `location.assign` when no router has registered. `roster-page.ts`'s own
 * `link()` is this function with a `navigate` callback threaded through instead; it is private to
 * that module and a page surface has no `navigate` in its options, so the seam is the shared part.
 */
function rowLink(text: string, href: string): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = 'setup-link';
  a.href = href;
  a.textContent = text;
  a.addEventListener('click', (ev) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navigateFromFrame(href);
  });
  return a;
}

/**
 * Build one row's action cell — a POST to `/api/<noun>/<verb>/<the row's paramFrom field>` for a
 * write action, and a LINK for one that opens a record (CAOS-1616).
 */
function renderRowActionCell(
  row: ControlPlaneRow,
  actions: readonly PageRowActionMeta[],
  doFetch: NonNullable<PageRenderOptions['controlPlaneFetch']>,
  onDone: () => void,
  reportHost: HTMLElement,
  objectHomes?: ReadonlyMap<string, Destination>,
): HTMLElement {
  const box = document.createElement('div');
  box.className = 'sa-page-actions sa-page-actions--row';
  for (const action of actions) {
    if (action.appliesWhen && !rowMatches(action.appliesWhen, row)) continue;

    // CAOS-1616 — an opening action, which goes somewhere instead of writing something. Before this
    // it could be authored, it deployed, and then this loop dropped it: no `param`, so `continue`,
    // and nothing anywhere said the button was missing. The two ways it can fail to resolve are
    // DIFFERENT FACTS and get different sentences, exactly as the record-action path does — an
    // empty field is about this row, a home-less object is about the workspace — and in both cases
    // the control is still drawn, disabled and explaining itself, rather than vanishing.
    if (isPageRowActionOpening(action)) {
      box.append(renderRowDestinationAction(action, row, objectHomes));
      continue;
    }
    if (!action.param || !action.paramFrom) continue; // schema-optional pair, defensively re-checked
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sa-page-action';
    btn.dataset['actionId'] = action.id;
    if (action.intent) btn.dataset['intent'] = action.intent;
    btn.textContent = action.label;
    btn.addEventListener('click', () => {
      const rowId = String(row[action.paramFrom!] ?? '');
      // CAOS-1892 — the outcome goes to the page's own durable report area, NOT beside this button.
      // `onDone()` re-reads the payload and `drawSections` rebuilds the sections host this cell
      // lives in, so a notice placed here would be destroyed by the very re-read that follows the
      // action that produced it — visible for an instant, or not at all.
      pressPageAction(
        action,
        `/api/${action.verb.noun}/${action.verb.verb}/${encodeURIComponent(rowId)}`,
        doFetch,
        reportHost,
        onDone,
        action.body,
        btn,
      );
    });
    box.append(btn);
  }
  return box;
}

/**
 * CAOS-1616 — draw one row action that OPENS a record: an anchor when the address resolves, and a
 * disabled control saying why when it does not.
 *
 * Never nothing. A roster row has no room for prose, so the explanation rides on the control itself
 * (`title`, plus `aria-disabled` so it is announced rather than merely greyed) — but the control is
 * present either way, because an action the author placed and the reader cannot see is the one
 * outcome this whole change is about.
 */
function renderRowDestinationAction(
  action: PageRowActionMeta & { readonly destination: ActionDestinationMeta },
  row: ControlPlaneRow,
  objectHomes?: ReadonlyMap<string, Destination>,
): HTMLElement {
  const rawId = row[action.destination.field];
  const targetId = rawId == null ? '' : String(rawId);
  const home = objectHomes?.get(action.destination.object);

  const unreachable =
    targetId === ''
      ? `“${action.label}” needs a value this row does not have.`
      : home === undefined
        ? `“${action.label}” opens a record of “${action.destination.object}”, and there is nowhere in this workspace those records are shown.`
        : undefined;

  if (unreachable !== undefined) {
    const dead = document.createElement('button');
    dead.type = 'button';
    dead.className = 'sa-page-action';
    dead.dataset['actionId'] = action.id;
    if (action.intent) dead.dataset['intent'] = action.intent;
    dead.textContent = action.label;
    dead.disabled = true;
    dead.setAttribute('aria-disabled', 'true');
    dead.title = unreachable;
    return dead;
  }

  const link = document.createElement('a');
  link.className = 'sa-page-action';
  link.dataset['actionId'] = action.id;
  if (action.intent) link.dataset['intent'] = action.intent;
  link.textContent = action.label;
  link.href = recordAddress(home!, targetId);
  return link;
}

/**
 * Order a page's blocks by the REGION each one was placed in (CAOS-388 / CAOS-393).
 *
 * A section names the template region it sits in, and until now that name was read off the manifest
 * and thrown away — so a page whose author put one block in `sidebar` and another in `main` drew them
 * in whatever order they happened to be written. The blueprint declares its regions in an order; that
 * order is what the page is supposed to draw in.
 *
 * Two deliberate limits, both stated in the pull request rather than guessed at on screen:
 *   • the template's GEOMETRY (its grid) is not applied — the regions stack, which is exactly what
 *     the platform's own single-region fallback does and is a legitimate rendering, not a wrong one;
 *   • if this workspace does not have the named blueprint, authored order stands. A page naming a
 *     blueprint the org lacks is refused at deploy, so reaching this means the template arrived after
 *     the page did.
 *
 * Stable within a region, and a section naming a region the blueprint does not declare keeps its
 * place at the end rather than being dropped — never dropped, on the same rule as everything else here.
 */
/**
 * CAOS-428 — place each section INSIDE its region, and lay the regions out.
 *
 * A page template declares named regions and, optionally, the geometry that arranges them. Ordering
 * the sections so a region's blocks come out together is not the same as putting them in different
 * places: before this, every section was appended into one flat column, so a two-region template and
 * a one-region template drew identically and the only way to see where one region ended was to read
 * the contents and infer it. That is precisely what a reader should never have to do.
 *
 * THE RULE, which lives with the gutter in the design system (`--caos-region-gutter`): adjacent
 * regions must be distinguishable without reading their contents.
 *
 * The template is unchanged by this and stays exactly as pure as it was — geometry and named
 * regions, no pixel widths, no styling. The delineation is the design system's, and the alignment is
 * asserted here rather than declared by anyone: regions sharing a row start at the same top edge
 * because the grid makes them, not because a template asked for it.
 */
function placeSectionsInRegions(
  body: HTMLElement,
  page: PageMeta,
  object: ObjectMeta | undefined,
  options: PageRenderOptions,
): void {
  const ordered = orderSectionsByRegion(page, options.meta);
  const template = options.meta.pageTemplates.get(page.template);
  const regions = template?.regions ?? [];

  // A section that names no region falls to the primary region — the blueprint's first — which is
  // the same rule `orderSectionsByRegion` applies when sorting them.
  const primaryKey = regions[0]?.key;
  const byRegion = new Map<string, PageSectionMeta[]>();
  for (const section of ordered) {
    const key = section.region ?? primaryKey;
    if (key === undefined) continue;
    const bucket = byRegion.get(key);
    if (bucket) bucket.push(section);
    else byRegion.set(key, [section]);
  }

  // Only regions that actually hold something. An empty region is not a hole a reader should have to
  // account for, and a reserved grid cell with nothing in it reads as content that failed to arrive.
  const occupied = regions.filter((r) => (byRegion.get(r.key)?.length ?? 0) > 0);

  // Sections naming a region the blueprint does not declare. `orderSectionsByRegion` ranks those at
  // MAX_SAFE_INTEGER, which sorts them to the end and RETURNS them — so the flat rendering this
  // replaced drew them, and placing by declared region alone would quietly lose them. A template
  // edited to rename a region would then drop that page's content with no error and no trace, which
  // is far worse than drawing it somewhere plain. They keep their place at the end.
  const declared = new Set(regions.map((r) => r.key));
  const unplaced = ordered.filter((s) => s.region !== undefined && !declared.has(s.region));

  // Fewer than two occupied regions means there is nothing to tell apart, so the DOM stays exactly
  // as it was rather than gaining a wrapper that changes every existing page to no visible end.
  if (occupied.length < 2) {
    for (const section of ordered) body.append(renderPageSection(page, section, object, options));
    return;
  }

  body.classList.add('sa-sections--regions');
  const geometry = template?.geometry;
  const occupiedKeys = new Set(occupied.map((r) => r.key));
  // A row none of whose cells hold anything is dropped rather than declared empty. The row itself
  // sizes to nothing, but the gutter either side of it does not, so a record page whose template
  // has a highlights band and whose page has no highlights section would open with a band of dead
  // space above its content — which reads as content that failed to arrive. Columns are NOT pruned
  // the same way: an unoccupied column holds its share of the width open, and that is the
  // template's proportion being honoured rather than a hole.
  const rows = (geometry?.areas ?? []).filter((row) =>
    row
      .trim()
      .split(/\s+/)
      .some((cell) => occupiedKeys.has(cell)),
  );
  if (geometry && rows.length > 0) {
    body.style.gridTemplateAreas = rows.map((row) => `"${row}"`).join(' ');
    body.style.gridTemplateColumns = geometry.cols;
  }
  // Which region keys the matrix actually names. A region placed on an area the matrix never
  // mentions is auto-placed by the browser wherever it happens to fit, which is a silently wrong
  // layout; leaving `grid-area` unset puts it in normal flow instead, which is merely plain.
  const named = new Set(rows.flatMap((row) => row.trim().split(/\s+/)).filter((cell) => cell !== '.'));

  for (const region of occupied) {
    const el = document.createElement('div');
    el.className = 'sa-region';
    el.dataset['region'] = region.key;
    if (rows.length > 0 && named.has(region.key)) el.style.gridArea = region.key;
    for (const section of byRegion.get(region.key) ?? []) {
      el.append(renderPageSection(page, section, object, options));
    }
    body.append(el);
  }

  if (unplaced.length > 0) {
    // Full width on a row of their own after the matrix — the end of the page, which is where they
    // were before. Left to auto-placement they would land in whichever cell the browser found free,
    // i.e. somewhere nobody authored and which changes as the region set changes.
    const tail = document.createElement('div');
    tail.className = 'sa-region sa-region--unplaced';
    tail.style.gridColumn = '1 / -1';
    for (const section of unplaced) tail.append(renderPageSection(page, section, object, options));
    body.append(tail);
  }
}

function orderSectionsByRegion(page: PageMeta, meta: OrgMetadata): readonly PageSectionMeta[] {
  const sections = page.sections ?? [];
  const template = meta.pageTemplates.get(page.template);
  if (!template || template.regions.length === 0) return sections;
  const order = new Map(template.regions.map((r, i) => [r.key, i]));
  // A section that names no region has not chosen one and falls to the primary region — the
  // blueprint's first — which is the same rule the platform's placement engine applies.
  const rank = (s: PageSectionMeta): number =>
    s.region === undefined ? 0 : (order.get(s.region) ?? Number.MAX_SAFE_INTEGER);
  return [...sections]
    .map((s, i) => ({ s, i, r: rank(s) }))
    .sort((a, b) => (a.r - b.r) || (a.i - b.i))
    .map((e) => e.s);
}

/** `a, b and c` — for naming several things in one sentence without a bare comma list. */
function listPhrase(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Draw one titled block of the page. */
function renderPageSection(
  page: PageMeta,
  section: PageSectionMeta,
  object: ObjectMeta | undefined,
  options: PageRenderOptions,
): HTMLElement {
  const sec = document.createElement('caos-detail-section');
  if (section.label) sec.setAttribute('heading', section.label);
  sec.dataset['section'] = section.id;

  const grid = document.createElement('div');
  grid.className = 'sa-field-grid';
  // CAOS-388 — the DISPLAY default lives here, where it is a rendering choice, rather than in the
  // projection, where it would have been indistinguishable from something the author asked for.
  grid.style.setProperty('--sa-cols', String(section.columns ?? 2));

  // Same two statements, one level down. A block nobody has filled in yet is a normal state of a
  // page being built, and it says so rather than leaving a titled box with nothing under it.
  if (section.items === undefined) {
    grid.append(
      pageNotice(
        'This block has not said what it holds',
        'Nothing is listed in it yet, so there is nothing to draw here.',
        'status',
      ),
    );
  } else if (section.items.length === 0) {
    grid.append(pageNotice('Nothing is placed here yet', 'This block is empty.', 'status'));
  } else {
    for (const item of section.items) {
      grid.append(
        item.kind === 'field'
          ? renderFieldItem(page, item, object, options)
          : item.kind === 'component'
            ? renderComponentItem(page, item, options)
            : item.kind === 'embed'
              ? renderEmbedItem(item, options)
              : renderUnknownItem(item),
      );
    }
  }

  sec.append(grid);
  return sec;
}

/**
 * Draw one placed FIELD.
 *
 * A field placement means "the value this record holds here", so it only means anything when the
 * page is bound to a record type that actually has that field. Both ways that can fail name what is
 * missing, because "a field did not appear" is unfindable and "Billing City is not on Account" is
 * a thing somebody can go and fix.
 */
function renderFieldItem(
  page: PageMeta,
  item: PageFieldItemMeta,
  object: ObjectMeta | undefined,
  options: PageRenderOptions,
): HTMLElement {
  if (!page.object) {
    return pageNotice(
      `“${item.fieldKey}” has nothing to read from`,
      'This page places a field, but it is not about a record type, so there is no record for the field to take its value from. Bind the page to a record type, or place a piece here instead.',
    );
  }
  if (!object) {
    return pageNotice(
      `The record type “${page.object}” is not installed here`,
      `This page is about “${page.object}”, and this workspace does not have it, so its fields cannot be read.`,
    );
  }
  const field = object.fields.get(item.field);
  if (!field) {
    return pageNotice(
      `“${item.field}” is not a field on ${object.label}`,
      `This page places a field that ${object.label} does not have. Add the field to ${object.label}, or take it off the page.`,
    );
  }

  const row = document.createElement('caos-key-value-row');
  row.setAttribute('label', field.label);
  row.setAttribute('stacked', '');
  row.setAttribute('no-border', '');
  row.dataset['item'] = item.id;
  const record = options.record;
  if (record) {
    const shown = record.display?.[field.apiName] ?? record[field.apiName];
    if (shown !== null && shown !== undefined && shown !== '') row.setAttribute('value', String(shown));
  }
  // No record open, or an empty value: the row keeps its name and shows the em dash the component
  // draws for "nothing here", which is a labelled field with no value — not a blank space.
  return row;
}

/** The sentence for "installed, but the code is not here" — one place, so both routes to it agree. */
function componentCodeMissingNotice(label: string): HTMLElement {
  return pageNotice(
    `“${label}” cannot be drawn here`,
    `“${label}” is installed in this workspace, but the code that draws it could not be loaded, so there is nothing to show. The package that ships it needs to be deployed to this workspace.`,
  );
}

/**
 * Draw one placed COMPONENT.
 *
 * Four outcomes now, where there used to be three:
 *
 *   1. the workspace has no component by that key    — it is not installed here;
 *   2. it has one, its element is not registered, and its manifest names no bundle to try — its
 *      code is not here, and there is nothing to fetch;
 *   3. it has one, its element is not registered, and its manifest DOES name a bundle — the code
 *      is fetched and loaded from where the manifest says it lives (CAOS-577), and the element is
 *      drawn optimistically while that happens, because a custom element created before its tag is
 *      defined draws nothing on its own and is upgraded by the browser the moment the definition
 *      lands (whether the load finishes before or after this element is attached to the page);
 *   4. it has one and the code is already present     — unchanged: the element is created and the
 *      placement's stored values are put on it, with no fetch at all.
 *
 * Outcome 2 is the case that used to be invisible for a real reason rather than merely rare: a
 * component row exists in the org's metadata, so every list says the piece is there, while the
 * bundle that draws it never reached this workspace. Outcome 3 is what makes it reachable for the
 * right reason today, instead of only because a test injected a registry answer that could never
 * happen in production — every first-party piece is compiled into the workspace, so outcome 2 could
 * only ever be exercised by a component that is not ours (see `host/test/page-surface.test.ts`).
 * If the fetch genuinely fails — network, a bad path, a cross-origin bundle this workspace refuses
 * to load (see {@link defaultLoadComponentModule}) — the optimistic element is replaced with the
 * same sentence outcome 2 already had, naming the piece exactly as before.
 */
/**
 * CAOS-497 — a card placed on a PAGE opens the route it carries.
 *
 * `card_grid` and `kanban_board` both announce an activated card as `caos-card-open` (`{ id }`) and
 * neither reads `href` itself, because the card does not know where it sits. Until now the only
 * listener was `collection-surface.ts`, which is the record-list path: there the id IS a record id
 * and the surface routes by record. A grid placed as a page section has no record behind it, so
 * nothing was listening and every card was a focusable button that did nothing when clicked —
 * `href` was declared on the item type and read by no one.
 *
 * So the id is resolved back to the authored item and its `href` is what navigates. An item with no
 * `href` stays inert deliberately: that is how the Developer Tools landing page ships eleven cards
 * naming destinations that do not exist yet without eleven of them pretending to work.
 */
function bindCardNavigation(el: HTMLElement, item: PageComponentItemMeta): void {
  const items = (item.props as { items?: unknown } | undefined)?.items;
  if (!Array.isArray(items)) return;

  const hrefById = new Map<string, string>();
  for (const entry of items) {
    if (entry === null || typeof entry !== 'object') continue;
    const { id, href } = entry as { id?: unknown; href?: unknown };
    if (typeof id === 'string' && typeof href === 'string' && href !== '') hrefById.set(id, href);
  }
  if (hrefById.size === 0) return;

  el.addEventListener('caos-card-open', (ev) => {
    const id = String((ev as CustomEvent<{ id: string }>).detail?.id ?? '');
    const href = hrefById.get(id);
    if (href !== undefined) navigateFromFrame(href);
  });
}

function renderComponentItem(page: PageMeta, item: PageComponentItemMeta, options: PageRenderOptions): HTMLElement {
  // CAOS-402 — decided BEFORE the piece is created, not after. A read scoped to a record cannot be
  // asked when no record is open behind the page, and that is a normal state of a record page, not
  // a failure. It has to be answered by drawing something else INSTEAD of the piece: the decorate
  // hooks below run while the element is still detached, so a piece cannot replace itself there.
  // Only once the piece is known to BE here: "this workspace has not got that piece" is a durable
  // configuration fact and the more actionable sentence, where "no record is open" is transient.
  // Answering the transient one first would hide the durable one behind it.
  if (item.read && options.meta.components.some((c) => c.key === item.key)) {
    const path = placedReadPath(page, item.read, options);
    if (path === null) {
      return pageNotice(
        'Nothing is open here yet',
        'This block shows the history of the record on this page, and no record is open behind it.',
        'status',
      );
    }
  }
  return renderInstalledComponent(item.key, item.props, options, {
    // BEFORE the props, exactly where it was: a registered custom element's property setters run
    // synchronously during `applyProps`, so one that reads its item id would have found it missing
    // if this moved after. The split into before/after is what keeps the extraction
    // behaviour-preserving rather than merely equivalent-looking.
    beforeProps: (el) => {
      el.dataset['item'] = item.id;
    },
    afterProps: (el) => {
      appendPlacedChildren(page, el, item, options);
      bindCardNavigation(el, item);
      // CAOS-1575 — the read and the write are two halves of one placement and share one piece of
      // state: what the read last returned. `loaded` is kept apart from `payload` because a read
      // that answered with a null payload and a read that has not answered are different facts, and
      // a save must refuse in the second case rather than send a bound field it never received.
      const held: { payload: unknown; loaded: boolean } = { payload: undefined, loaded: false };
      const refresh = item.read
        ? feedPlacedRead(page, el, item.read, options, (payload) => {
            held.payload = payload;
            held.loaded = true;
          })
        : () => {};
      // CAOS-1576's figure is fed here, unchanged and after the read, so the order main
      // established between the two fetches is the order this keeps.
      if (item.report) feedPlacedReport(el, item.report, options);
      if (item.write) wirePlacedWrite(el, item.write, options, held, refresh);
    },
  });
}

/**
 * CAOS-402 — the address a placed piece's read is fetched from, or `null` when a parameter it
 * declared has nothing to bind to.
 *
 * `null` is a real answer and not an error: a page bound to a record type draws perfectly well with
 * no record open behind it (that is what a layout looks like before anything is loaded into it), and
 * a read scoped to a record cannot be ASKED in that state. Returning a path with an empty record id
 * would turn "nothing is open" into a query for the record whose id is the empty string, which the
 * route would answer — emptily, and indistinguishably from a record that genuinely has no history.
 */
function placedReadPath(page: PageMeta, read: PageReadMeta, options: PageRenderOptions): string | null {
  const query: string[] = [];
  for (const [name, source] of Object.entries(read.params ?? {})) {
    // CAOS-798's route parameters first, then the record open behind the page. Both are the same
    // fact from two directions, and the route is the one that named it.
    //
    // CAOS-1575 — `urlParameter` is the route parameter SPELLED THE SAME as the request parameter it
    // fills. Read by that name and by no other: the binding cannot remap, which is what keeps a page
    // from pointing one request parameter at a route parameter that means something else. Falls
    // through to `null` below when the route does not carry it, exactly as a record-scoped read does
    // with no record open — "opened without one" must not become a query for the empty string.
    const value =
      source === 'objectKey'
        ? page.object
        : source === 'urlParameter'
          ? options.routeParams?.[name]
          : (options.routeParams?.['recordId'] ?? asIdString(options.record?.['id']));
    if (value === undefined || value === '') return null;
    query.push(`${encodeURIComponent(name)}=${encodeURIComponent(value)}`);
  }
  return `/api/${read.noun}/${read.verb}${query.length > 0 ? `?${query.join('&')}` : ''}`;
}

/** A record id as the query string needs it, or `undefined` for anything that is not one. */
function asIdString(raw: unknown): string | undefined {
  if (typeof raw === 'string') return raw.length > 0 ? raw : undefined;
  if (typeof raw === 'number') return String(raw);
  return undefined;
}

/**
 * CAOS-402 — fetch a placed piece's record-scoped read and hand it the rows.
 *
 * The piece does no fetching of its own, which is the rule every other live surface here already
 * follows: a component is fed rows AS DATA and stays a pure renderer, so the one place a route name
 * becomes an address is the injected fetch seam. That is a boundary, not a detail.
 *
 * Every way this can come up short says so on the screen, in the words a person would use, because
 * this whole surface exists to stop "nothing to draw" and "drew nothing" looking identical from
 * outside. A piece fed from a read that failed must not sit there looking like a record with no
 * history — that is the same false statement in a new place.
 */
function feedPlacedRead(
  page: PageMeta,
  el: HTMLElement,
  read: PageReadMeta,
  options: PageRenderOptions,
  /**
   * CAOS-1575 — called with the read's PAYLOAD each time it answers. A placed write binds body
   * fields from it (`expectedVersion` from `componentVersion`), and this is the only place that
   * value exists: it is server-returned data held for the piece that was fed it, never re-fetched
   * by the write, so the version a save states back is the version the person was actually shown.
   */
  onPayload?: (payload: unknown) => void,
): () => void {
  // The unaskable case is settled by {@link renderComponentItem} before the piece exists, so a path
  // is always in hand here.
  const path = placedReadPath(page, read, options);
  if (path === null) return () => {};

  const doFetch = options.controlPlaneFetch ?? defaultControlPlaneFetch;
  const run = (): void => {
  Promise.resolve()
    .then(() => doFetch(path))
    .then(async (res) => {
      if (!res.ok) throw new Error(`the read answered ${path} with an error`);
      const body = await res.json();
      // Every kernel route answers through the success envelope (`kernel/src/api/substrate.ts`),
      // so the payload is `data` and a body that is not an envelope is a FAILED read, never an
      // empty panel. CAOS-788 is the reason this is explicit: a sibling surface read `rowsAt`
      // straight off the body, against a shape no route sends, and drew empty behind a green suite.
      const envelope = body as { ok?: unknown; data?: unknown } | null;
      if (envelope === null || typeof envelope !== 'object' || envelope.ok !== true) {
        throw new Error(`the read answered ${path} with something that is not a success envelope`);
      }
      const payload = envelope.data;
      const rows = read.rowsAt === undefined ? payload : readJsonPath(payload, read.rowsAt);
      // An `undefined` here means the payload did not hold what the page said it would — a
      // different statement from "it held an empty list", and the two must not collapse.
      if (rows === undefined || rows === null) throw new Error(`the read returned nothing at "${read.rowsAt}"`);
      // CAOS-1575 — the payload goes to the write BEFORE the piece is fed, so a save raised from
      // the piece the instant it draws still has the version that came with what it is showing.
      onPayload?.(payload);
      // CAOS-1575 — `rows` unless the placement named another input, and converted first if the
      // placement said how. Every placement authored before these existed is unchanged: it names
      // neither, so this is `{ rows }` applied as it came, exactly as before.
      //
      // `jsonText` serialises, deliberately including a payload that is ALREADY a string — `"abc"`
      // is the JSON text of `abc`, and a special case for strings would make the word mean two
      // things depending on data the author cannot see when writing the page.
      const applied = read.intoAs === 'jsonText' ? JSON.stringify(rows, null, 2) : rows;
      // Set as a PROPERTY, never through `applyProps`. That helper is for a placement's AUTHORED
      // values, where a string is naturally an attribute — and it would put this one in the DOM as
      // `value="..."`, which `caos-code-editor` does not observe and whose own doc says, in terms,
      // "a property and not an attribute: an artifact does not belong in the DOM as text". A fetched
      // payload is data, not authoring, and it has always in effect gone to a property: the only
      // thing a read could feed before `into` was `rows`, an array, which `applyProps` assigns as a
      // property anyway. So this changes nothing for any placement that exists and is the only way
      // a TEXT payload can reach a piece at all.
      (el as unknown as Record<string, unknown>)[propertyName(read.into ?? 'rows')] = applied;
    })
    .catch(() => {
      if (el.parentNode) {
        el.replaceWith(
          pageNotice(
            'This block could not be loaded',
            `Its contents come from “${read.noun} ${read.verb}”, and that did not answer. Nothing is missing from the record itself.`,
          ),
        );
      }
    });
  };
  run();
  return run;
}

/**
 * CAOS-1575 — prototype-chain identifiers refused as a destination path segment, mirroring the
 * kernel's `RESERVED_IDENTIFIERS` (CAOS-1354) and the projection's own copy. Three layers deny this:
 * deploy refuses the page, the projection drops the write, and this refuses the save. The chain is
 * also made structurally unreachable in {@link writeJsonPath} — a denial you can forget to apply is
 * not the same as a shape that cannot be expressed.
 */
const RESERVED_WRITE_PATH_SEGMENTS: ReadonlySet<string> = new Set([
  'constructor',
  'prototype',
  '__proto__',
  '__defineGetter__',
  '__defineSetter__',
  '__lookupGetter__',
  '__lookupSetter__',
]);

/**
 * CAOS-1575 — write `value` at a dotted `path` inside `target`, creating the objects on the way.
 *
 * ── Why every object here has a NULL PROTOTYPE ────────────────────────────────────────────────────
 * This is the only place in the page vocabulary where an authored string becomes a plain-object
 * assignment target, and that makes the prototype chain reachable. On an ordinary `{}`, reading
 * `at['__proto__']` returns `Object.prototype` — an object, and not an array, so a "is this already
 * an object?" guard walks INTO it happily and the final assignment mutates the prototype every other
 * object on the page inherits from. `component.__proto__.isAdmin` is the shape that does it, and it
 * passes a gate that only inspects a path's first segment.
 *
 * Deploy refuses a reserved segment and the projection drops such a write, so this is the third
 * line, not the only one. It is here because the other two are policy and this is arithmetic: with
 * no prototype there is no chain to reach, so `__proto__` is an ordinary own property of a throwaway
 * object and means nothing. `JSON.stringify` treats these exactly as it treats `{}`.
 */
function writeJsonPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const segments = path.split('.');
  let at = target;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const key = segments[i]!;
    const next = Object.prototype.hasOwnProperty.call(at, key) ? at[key] : undefined;
    // A segment already holding a non-object is REPLACED rather than written through: two bindings
    // whose paths disagree about the shape (`component` and `component.type`) would otherwise have
    // the later one silently mutate a string. Deploy cannot see this — the paths are independent
    // keys — so the renderer has to pick one, and building the object the deeper path needs is the
    // reading that lets the write reach the route at all.
    if (next === null || typeof next !== 'object' || Array.isArray(next)) {
      at[key] = Object.create(null) as Record<string, unknown>;
    }
    at = at[key] as Record<string, unknown>;
  }
  at[segments[segments.length - 1]!] = value;
}

/**
 * CAOS-1575 — surface a sentence beside a piece without destroying what the person is holding.
 *
 * Deliberately NOT `el.replaceWith`, which is what a failed READ does. A failed read has nothing to
 * lose; a failed SAVE is sitting next to text somebody typed, and replacing the editor with an
 * error message would throw the edit away at the exact moment it became the only copy. So the
 * notice goes beside the piece, and one notice replaces the previous one rather than accumulating.
 */
function noticeBeside(el: HTMLElement, title: string, body: string): void {
  const previous = el.nextElementSibling;
  const notice = pageNotice(title, body);
  notice.dataset['writeNotice'] = 'true';
  if (previous instanceof HTMLElement && previous.dataset['writeNotice'] === 'true') previous.replaceWith(notice);
  else el.after(notice);
}

/**
 * CAOS-1575 — wire a placed piece's WRITE: when the piece raises the event the write names, build
 * the request body from the bound sources and POST it.
 *
 * ── The body is BOUND, never composed ────────────────────────────────────────────────────────────
 * Every value put on the wire here comes from one of four places and none of them is the page:
 * what the piece holds, the route parameter spelled like the destination's last segment, or a named
 * field of the read's own last payload. There is no branch in which author-written text becomes a
 * value — that is the property `PlacedWriteValueSourceSchema` documents, and this function is where
 * it is either true or not.
 *
 * ── Failing before the request, rather than through it ───────────────────────────────────────────
 * A binding that cannot be resolved stops the save HERE. The alternative — send the field absent
 * and let the route refuse — produces a true error about the wrong thing: `component-upsert` would
 * answer "expectedVersion is required" to somebody whose actual problem is that the screen was
 * opened without a route parameter. The refusal a person meets should name what they can act on.
 *
 * The one refusal deliberately NOT pre-empted is the stale version, because it is the ticket's
 * whole point: `expectedVersion` is sent as read, `component-upsert` compares it inside its apply
 * transaction, and somebody who saved second is refused BY THE SERVER with what the route says.
 * That message is shown verbatim rather than replaced with a generic sentence — a conflict that
 * reads "could not save" is indistinguishable from a network failure, and the two call for
 * opposite responses.
 */
function wirePlacedWrite(
  el: HTMLElement,
  write: PageWriteMeta,
  options: PageRenderOptions,
  /** Holds the read's last payload; mutated by {@link feedPlacedRead} as the read answers. */
  held: { payload: unknown; loaded: boolean },
  /** Re-run the read, so the piece holds the version its next save must state back. */
  refresh: () => void,
): void {
  const doFetch = options.controlPlaneFetch ?? defaultControlPlaneFetch;
  el.addEventListener(write.on, (event) => {
    const detail = (event as CustomEvent<unknown>).detail;
    const value =
      detail !== null && typeof detail === 'object' && !Array.isArray(detail)
        ? (detail as Record<string, unknown>)['value']
        : undefined;

    // Null prototype for the ROOT as well as the objects `writeJsonPath` creates inside it: the root
    // is the first thing a top-level segment indexes into, so an ordinary `{}` here would leave the
    // chain reachable at depth 1 however safe the intermediates were.
    const body: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
    for (const [path, binding] of Object.entries(write.body)) {
      // Refused before anything is bound, not merely dropped: a path reaching through the prototype
      // chain is not a field this piece may write, and half a body is not a save worth sending.
      if (path.split('.').some((seg) => RESERVED_WRITE_PATH_SEGMENTS.has(seg.toLowerCase()))) {
        noticeBeside(el, 'This could not be saved', 'This screen is misconfigured and the save was not sent.');
        return;
      }
      let bound: unknown;
      switch (binding.from) {
        case 'componentValue':
          bound = value;
          break;
        case 'componentValueAsJson': {
          if (typeof value !== 'string') {
            noticeBeside(el, 'This could not be saved', 'The editor did not hand over any text to save.');
            return;
          }
          try {
            bound = JSON.parse(value);
          } catch {
            // Named precisely, and caught HERE on purpose: sent as a string, the route would refuse
            // it as a malformed component, which describes the wrong problem to the one person who
            // can fix it and is holding the text that would fix it.
            noticeBeside(el, 'This is not valid JSON', 'Nothing was saved. Correct the source and save again.');
            return;
          }
          break;
        }
        case 'urlParameter': {
          // Same-name-only, as a read parameter is: the LAST SEGMENT of the destination path names
          // the route parameter, and nothing in the page can point it somewhere else.
          bound = options.routeParams?.[path.split('.').pop()!];
          break;
        }
        case 'payload': {
          if (!held.loaded) {
            noticeBeside(el, 'This could not be saved yet', 'What is on screen has not finished loading, so there is nothing to save against.');
            return;
          }
          bound = readJsonPath(held.payload, binding.name!);
          break;
        }
      }
      if (bound === undefined) {
        noticeBeside(el, 'This could not be saved', `The screen has no value for “${path}”, so the save was not sent.`);
        return;
      }
      writeJsonPath(body, path, bound);
    }

    void (async () => {
      try {
        // No content-type set, matching the row action that already POSTs a JSON body through this
        // same seam: the seam's init is deliberately just `{ method, body }`, and widening it for
        // one caller would leave two shapes of control-plane POST to keep in step.
        const res = await doFetch(`/api/${write.noun}/${write.verb}`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          // The route's own sentence, verbatim where there is one. A stale-version conflict is the
          // refusal this whole placement exists to make reachable, and it has to arrive as itself.
          const answered = (await res.json().catch(() => undefined)) as
            | { error?: { message?: unknown } }
            | undefined;
          const said = answered?.error?.message;
          noticeBeside(
            el,
            'This was not saved',
            typeof said === 'string' && said.length > 0
              ? said
              : `“${write.noun} ${write.verb}” refused the save and did not say why.`,
          );
          return;
        }
        // The version this piece holds is now the one BEFORE the save, so the next save would be
        // refused against a version that is only stale in this browser. Re-reading is what makes a
        // second save possible at all.
        refresh();
      } catch {
        noticeBeside(el, 'This was not saved', `“${write.noun} ${write.verb}” could not be reached. Nothing was changed.`);
      }
    })();
  });
}

/**
 * CAOS-1576 — fetch the stored report a placed piece names, and hand it the figure.
 *
 * An authored page could show a RECORD but not a FIGURE ABOUT MANY records, so every surface
 * whose content was a measurement had to be written as host code. `data/report` has measured a
 * whole visible set server-side since CAOS-537; what was missing was any way for metadata to ask
 * it. The placement names a report key, and this is where that key becomes an address.
 *
 * THE FIGURE IS NEVER COMPUTED HERE. Not one number on this path is added up in the browser: the
 * answer arrives measured over the whole visible set, which is the only way a headline can be
 * trusted not to disagree with the records behind it. Paging rows in and summing them would be
 * wrong twice over — it would measure only the page that happened to load, and it would do it
 * without the field security the server applies.
 *
 * IT CANNOT ASK FOR ANOTHER PRINCIPAL'S DATA, and that is structural rather than checked. The
 * only thing interpolated is a report KEY that came through the deploy gate; there is no
 * parameter bag, and nowhere in the shape to put a person or an org. The server then answers as
 * whoever is looking, so two readers of one page see the numbers their own access allows.
 *
 * The whole answer is handed over as one `figure` prop rather than unpacked into several. A
 * report answers `{ label, object, fn, value }`, or `{ …, groupBy, buckets }` when it groups, and
 * a piece drawing it usually wants the label beside the number. Passing an object also keeps a
 * legitimate zero drawable: `applyProps` skips a null and an undefined alike, so a bare
 * `value: null` from an average over an empty set would silently never reach the element.
 */
function feedPlacedReport(el: HTMLElement, report: string, options: PageRenderOptions): void {
  const path = `/api/data/report?report=${encodeURIComponent(report)}`;
  const doFetch = options.controlPlaneFetch ?? defaultControlPlaneFetch;
  Promise.resolve()
    .then(() => doFetch(path))
    .then(async (res) => {
      if (!res.ok) throw new Error(`the report answered ${path} with an error`);
      const body = await res.json();
      // The same envelope rule the read path above states, for the same CAOS-788 reason: every
      // kernel route answers through the success envelope, so a body that is not one is a FAILED
      // read rather than a figure of nothing.
      const envelope = body as { ok?: unknown; data?: unknown } | null;
      if (envelope === null || typeof envelope !== 'object' || envelope.ok !== true) {
        throw new Error(`the report answered ${path} with something that is not a success envelope`);
      }
      const figure = envelope.data;
      // A report answers with an object carrying the measurement. Anything else means the route
      // did not answer the question asked, which must read as a failure rather than as a blank
      // number — a figure that quietly draws empty is the false statement this surface exists to
      // stop.
      if (figure === null || typeof figure !== 'object') {
        throw new Error(`the report ${report} returned no figure`);
      }
      // No component manifest here, so nothing is declared assign-only: a host-drawn region's
      // `figure` is this function's own value, not one an author placed.
      applyProps(el, { figure }, EMPTY_PROPERTY_ONLY);
    })
    .catch(() => {
      if (el.parentNode) {
        el.replaceWith(
          pageNotice(
            'This figure could not be worked out',
            `It is measured by the “${report}” report, and that did not answer. The records themselves are unaffected.`,
          ),
        );
      }
    });
}
/**
 * Draw one INSTALLED component by key, with `props` applied — the four outcomes described above,
 * and the one place they are decided.
 *
 * Extracted from {@link renderComponentItem} by CAOS-560 so a record ACTION of the `component` kind
 * opens a piece the same way a page places one. The alternative was a second copy of the
 * registered / not-registered / bundle-load ladder, which is precisely the sort of duplicate that
 * drifts: a component that loads on a page and silently fails to load from a button, for a reason
 * nobody could see, because the two paths had stopped agreeing.
 *
 * `decorate` is whatever the caller needs on the element beyond its props — a placement stamps its
 * item id and its children, an action puts the record in context. It is SPLIT around `applyProps`
 * because the order is observable: a registered element's property setters run synchronously as the
 * props are applied, so anything a setter might read has to be on the element first.
 */
/**
 * CAOS-561 — the bundles whose code was asked for and did not arrive, until the workspace is reloaded.
 *
 * A component that is installed and has a bundle CANNOT be known undrawable synchronously: the
 * whole point of CAOS-577 is to draw the element and let it upgrade in place when its code lands.
 * That is fine for a placement — the piece's own region says so when it fails. It is not fine for a
 * record ACTION THAT REPLACED A STANDARD VERB, because the affordance it displaced is already gone,
 * so "find out on the press" means find out with no way back.
 *
 * Remembering the failure closes the ladder's one asynchronous rung against the SECOND ask and
 * every ask after it. The first ask is closed by {@link renderInstalledComponent} telling its
 * caller, so the surface that displaced a verb can put itself right.
 *
 * IT NEVER FORGETS, and that is chosen rather than overlooked. A failure that was only a bad
 * moment on the network is remembered as broken until the browser reloads the workspace — this
 * is a module-level set, and moving between records and pages inside the workspace does not
 * reload the module, so the penalty outlasts the screen it was incurred on. Two
 * components sharing one bundle are both marked by either one failing. The alternative — expiring
 * it, or retrying — re-opens the hole this exists to close: a verb whose replacement is offered
 * again, displacing the standard affordance again, on a guess that this time the code will arrive.
 * Erring toward the standard affordance costs somebody the override until they reload the tab.
 * Erring the other way costs them the verb, with nothing on screen to say why. Reloading a page is
 * a thing people already know how to do.
 */
const codeThatDidNotLoad = new Set<string>();

/**
 * CAOS-561 — why {@link renderInstalledComponent} would refuse to draw `key`, as far as that is
 * knowable SYNCHRONOUSLY, or `null` when it would draw the piece.
 *
 * It walks the same ladder, in the same order, off the same inputs. That is the point of it being
 * here rather than a second opinion written where it is needed: a caller deciding for itself
 * whether a piece can be drawn is how a component that draws on a page and silently does not draw
 * from a button stops agreeing with itself.
 */
export function componentDrawFailure(key: string, options: PageRenderOptions): string | null {
  const installed = options.meta.components.find((c) => c.key === key);
  if (!installed) return `this workspace does not have “${key}”`;
  const isRegistered = options.isTagRegistered ?? defaultIsTagRegistered;
  if (isRegistered(installed.tag)) return null;
  if (!installed.bundle) return `“${installed.label}” has no code in this workspace`;
  if (codeThatDidNotLoad.has(installed.bundle.module)) return `“${installed.label}” has code that could not be loaded`;
  return null;
}

export function renderInstalledComponent(
  key: string,
  props: Readonly<Record<string, unknown>> | undefined,
  options: PageRenderOptions,
  decorate?: {
    beforeProps?: (el: HTMLElement) => void;
    afterProps?: (el: HTMLElement) => void;
    /**
     * CAOS-561 — called when the piece turns out not to be drawable: either immediately, because
     * this function returned a sentence instead of the element, or later, when its code fails to
     * arrive. A caller that took something else off the screen to make room for this piece is the
     * only one that needs it, and it is the only way that caller can hear about the asynchronous
     * case at all.
     */
    onUndrawable?: () => void;
  },
): HTMLElement {
  const installed = options.meta.components.find((c) => c.key === key);
  if (!installed) {
    decorate?.onUndrawable?.();
    return pageNotice(
      `“${key}” is not installed here`,
      `This page places a piece called “${key}”, and this workspace does not have it. Install the package that provides it, or take the piece off the page.`,
    );
  }

  const resolved = withDeclaredSources(props, installed, {
    routeParams: options.routeParams,
    viewer: options.viewer,
    record: options.record,
  });
  // CAOS-159 — read off the SAME manifest the props are being applied against, so a piece whose
  // declaration says an input is assign-only gets it assigned on both rungs of the ladder below.
  const propertyOnly = propertyOnlyInputs(installed);

  const isRegistered = options.isTagRegistered ?? defaultIsTagRegistered;
  if (isRegistered(installed.tag)) {
    const el = document.createElement(installed.tag);
    decorate?.beforeProps?.(el);
    applyProps(el, resolved, propertyOnly);
    decorate?.afterProps?.(el);
    return el;
  }

  // Not registered yet. Nothing to fetch — same outcome as always.
  if (!installed.bundle) {
    decorate?.onUndrawable?.();
    return componentCodeMissingNotice(installed.label);
  }

  // CAOS-577 — the manifest names where the code lives. Draw the element now (it upgrades in
  // place, natively, the moment its tag is defined) and load its code in the background; swap in
  // the "cannot be drawn" sentence only if that genuinely fails.
  //
  // CAOS-159 — AN ASSIGN-ONLY INPUT APPLIED ON THIS RUNG NEEDS DELIVERING TWICE. The element's tag
  // is not defined yet, so the assignment below lands as an own property that would shadow the
  // class's setter the moment the element upgrades. `redeliverAssignOnly` in the `then` under this
  // is what puts the value through the setter once there is one; see its own note for why the
  // upgrade has to be forced rather than waited for.
  const el = document.createElement(installed.tag);
  decorate?.beforeProps?.(el);
  applyProps(el, resolved, propertyOnly);
  decorate?.afterProps?.(el);
  const loadModule = options.loadComponentModule ?? defaultLoadComponentModule;
  Promise.resolve()
    .then(() => loadModule(installed.bundle!, installed.tag))
    .then(() => {
      if (!isRegistered(installed.tag)) {
        throw new Error(`"${installed.tag}"'s code loaded but did not register that element`);
      }
      redeliverAssignOnly(el, resolved, propertyOnly);
    })
    .catch(() => {
      // Remembered before anyone is told, so a caller that re-draws in response asks the ladder
      // again and gets the answer that has just been learned rather than the one it had.
      codeThatDidNotLoad.add(installed.bundle!.module);
      if (el.parentNode) el.replaceWith(componentCodeMissingNotice(installed.label));
      decorate?.onUndrawable?.();
    });
  return el;
}

/**
 * CAOS-654 — put what a placement holds inside the piece it drew, in order. Text goes in as text. A
 * piece goes in as whatever drawing it produced — the piece itself, or the sentence saying why it
 * cannot be drawn — and either way carries the slot it was placed in, so a failure shows where the
 * piece was meant to be instead of falling into the parent's default content.
 */
function appendPlacedChildren(page: PageMeta, el: HTMLElement, item: PageComponentItemMeta, options: PageRenderOptions): void {
  for (const child of item.children ?? []) {
    if (typeof child === 'string') {
      el.append(document.createTextNode(child));
      continue;
    }
    const drawn = child.kind === 'component' ? renderComponentItem(page, child, options) : renderUnknownItem(child);
    if (child.kind === 'component' && child.slot !== undefined) drawn.setAttribute('slot', child.slot);
    el.append(drawn);
  }
}

/**
 * Draw a placement this workspace has no renderer for.
 *
 * A page authored against newer code than this workspace is running can place a kind of thing this
 * build has never heard of. Dropping it would be the original defect exactly: the author placed
 * something and the screen shows nothing, with no way to tell that from a piece that draws nothing on
 * purpose.
 */
/**
 * CAOS-498 — draw another page inside this one.
 *
 * WHY THE PAGE IS RESOLVED HERE AND NOT CARRIED IN. Whether the org has the embedded page is a fact
 * about the ORG, not about the page doing the embedding — the same reason a placed component's key
 * is resolved at render. Deploy refuses an embed naming a page the org does not have, so a miss
 * here means the page was installed and the package that owned the other one was removed
 * afterwards. That is precisely the case that has to be NAMED: the alternative is a reader looking
 * at a gap where a screen used to be, with nothing anywhere saying why.
 *
 * WHAT IT DOES NOT DO IS RE-RESOLVE PERMISSION. The embedded page is drawn through the same
 * `renderPageSurface` a destination is drawn through, against the same `options.meta`, for whoever
 * is looking. There is no elevation here and nowhere to put one: the embed carries a page key and
 * nothing else, and every read the embedded page makes is made as the viewer with the viewer's own
 * object and field permissions — exactly as it is when that page is opened directly. Embedding a
 * page is a way to SHOW it in another place, never a way to see more of it.
 */
function renderEmbedItem(item: PageEmbedItemMeta, options: PageRenderOptions): HTMLElement {
  const depth = options.embedDepth ?? 0;
  // Deploy refuses cycles; this catches metadata installed before that gate existed, and says so
  // rather than stopping quietly. See `PageRenderOptions.embedDepth`.
  if (depth >= MAX_EMBED_DEPTH) {
    return pageNotice(
      // CAOS-492 — whichever arm was authored. Reading `pageKey` alone named `undefined` on a
      // roster embed, in the one notice whose whole job is to say WHICH surface could not be drawn.
      `“${item.rosterPageKey ?? item.pageKey ?? '(unnamed)'}” is nested too deeply to draw`,
      `Pages are embedded ${MAX_EMBED_DEPTH} levels deep here, which usually means one of them ends ` +
        'up embedding itself. Deploying these pages again will refuse the loop and name it.',
      'alert',
    );
  }
  // CAOS-492 — the ROSTER arm, resolved by the seam rather than from `meta.pages`. The bulk metadata
  // read carries pages and no roster bodies, which is why a roster cannot be looked up the way the
  // page below is and why `roster-destination.ts` fetches its spec one at a time. That module already
  // answers "this workspace does not have it" in the same words the page branch uses, so the absent
  // case is not re-answered here — a second copy of that sentence is how two routes to one surface
  // start disagreeing, which is what its own doc warns about.
  if (item.rosterPageKey !== undefined) {
    if (!options.embedRosterDraw) {
      return pageNotice(
        `“${item.rosterPageKey}” cannot be drawn here`,
        'This block draws a list, and this screen was built without the part that draws one. The ' +
          'list itself is fine; nothing on this page can show it.',
        'alert',
      );
    }
    const holder = document.createElement('div');
    holder.className = 'sa-page-embed';
    holder.dataset['embedsRoster'] = item.rosterPageKey;
    holder.append(options.embedRosterDraw(item.rosterPageKey, { ...options, embedded: true, embedDepth: depth + 1 }));
    return holder;
  }
  if (item.pageKey === undefined) {
    // Neither arm. Deploy refuses it on both gates and the projection drops it to `unknown`, so this
    // is unreachable from anything installed — and it is a named sentence rather than a cast for the
    // reason every branch in this file is: the alternative is a blank where a surface should be.
    return pageNotice(
      'This block does not say what it draws',
      'Something is embedded here without naming the screen or the list it is meant to show. ' +
        'Deploying the page again will refuse it and say so.',
      'alert',
    );
  }
  const embedded = options.meta.pages.get(item.pageKey);
  if (!embedded) {
    return pageNotice(
      `“${item.pageKey}” has nothing behind it`,
      `This block draws a page called “${item.pageKey}”, and this workspace does not have it. ` +
        'Install the package that provides it, or remove the block.',
    );
  }
  const holder = document.createElement('div');
  holder.className = 'sa-page-embed';
  holder.dataset['embeds'] = item.pageKey;
  holder.append(renderPageSurface(embedded, { ...options, embedded: true, embedDepth: depth + 1 }));
  return holder;
}

function renderUnknownItem(item: PageUnknownItemMeta): HTMLElement {
  return pageNotice(
    `This workspace does not know what a “${item.type}” is`,
    `Something of a kind called “${item.type}” is placed here, and this workspace has no way to draw ` +
      'it. It was most likely authored against a newer version than this workspace is running.',
  );
}

/**
 * Put the placement's stored values onto the element.
 *
 * A plain value (text, a number, a yes/no) goes on as an attribute, which is how every piece in the
 * library already takes one and what makes it visible in the markup. Anything with structure — a
 * list of rows, a set of options — goes on as a property, because an attribute can only ever hold a
 * string and stringifying a list of rows produces `[object Object]` on the screen.
 *
 * A value the component never declared does not reach here from a deployed page: deploy refuses it,
 * along with a missing required value and a value of the wrong type, naming the page, the piece and
 * the input (CAOS-604 / CAOS-625, `placementInputFindings` in kernel/src/deploy/validate.ts). The one
 * name a page sets that no component declares is `valueAt` on a control-plane page, which
 * {@link renderControlPlaneComponentItem} resolves and removes before the value is applied here.
 *
 * CAOS-590 — the name a placement stores a value under is the one the component's manifest declares,
 * and the kernel only accepts a lower_snake identifier there (`max_rows`, `medium_size`). A piece
 * observes the kebab-case attribute (`max-rows`) or the camelCase property (`maxRows`) of that same
 * name. Translating only a camelCase name left every two-word input a deployed page could carry on the
 * element under a spelling nothing reads, so both spellings are translated here.
 */
/**
 * CAOS-798 — fill a component's `urlParameter` inputs from the route that opened the surface.
 *
 * The DECLARED SOURCE WINS over a static value the page set for the same input. `source` says
 * where a value comes from, so a page that also hard-codes one is stating two different origins for
 * the same input, and the manifest's is the one the component was written against. Nothing relied on
 * the other reading: until this change no placed component received a route parameter at all.
 *
 * A declared parameter the route does not carry is left ABSENT rather than set to an empty string —
 * "opened without one" and "opened with a blank one" are different, and a component that must
 * distinguish them (a blank key is not a key) can only do so if this does not flatten them.
 *
 * THIS IS FOR SURFACES A ROUTE OPENS. It runs for every caller of {@link renderInstalledComponent},
 * but the other one — a record ACTION (`content-source.ts`'s `openComponentAction`) — has no route
 * to read: it is a button pressed on a record, and it hands the piece that record, its id and its
 * object as context instead. So `routeParams` is absent there and nothing is filled, which is
 * right rather than a gap. A piece meant to be opened both ways reads its record from the context
 * it is given and its route parameter from the input it declared; they are different questions.
 *
 * A `source` this does not recognise simply never resolves. The projection passes any declared
 * string through rather than checking it against the metatype's enum, because the host owning a
 * second copy of that enum is how a kernel that adds a source ends up rejected by a stale host.
 * The cost is that a misspelled source is silent — see the PR for why that trade was taken.
 *
 * CAOS-1808 — AND SO IS A CORRECTLY-SPELLED SOURCE NOBODY IMPLEMENTED, which is the larger half of
 * that cost and the reason the single hard-coded comparison became the table below. The metatype
 * declares six sources and this renderer answers one; the other five reach a placed component as
 * nothing at all, with no screen, log or refusal saying why. That silence has produced three
 * separate defects already — `urlParameter` itself (CAOS-798), `propertyOnly` (CAOS-159), and
 * `recordField`, still open as CAOS-1806 — each found by somebody walking into it months apart.
 *
 * {@link RESOLVED_INPUT_SOURCES} is what makes the fourth one hard to add quietly. It is derived
 * from the table rather than written down beside it, so it cannot drift from what this function
 * really answers, and a census holds it against the kernel's declared enum: a source that is
 * neither resolved here nor dispositioned there fails that suite.
 *
 * THE TABLE IS A `Map` AND NOT AN OBJECT LITERAL, deliberately. `source` is an author-supplied
 * string this file does not validate — that is the whole point of the paragraph above — so an
 * object-literal lookup would answer `'toString'` or `'constructor'` with something off
 * `Object.prototype` and then call it. A `Map` has no inherited keys, so the hazard cannot arise
 * rather than being guarded against.
 *
 * CAOS-1809 — AND `userContext` IS THE SECOND ENTRY.
 *
 * WHAT IT RESOLVES AGAINST, and why that is not a vocabulary this change invented. The CAOS-1808
 * census recorded this source as blocked on two things: "No key vocabulary exists, and the renderer
 * carries no viewer at all." The second half was true of {@link PageRenderOptions} and never of the
 * host, which has carried a viewer since CAOS-342 — `buildShellRuntime` builds `runtime.user` from
 * the session identity, and `shell-frame.ts` hands `runtime.user.name` to `caos-topbar`'s
 * `user-name` attribute by hand. So the value existed and reached this exact input on the
 * hand-built path while the declared metadata contract for it resolved to nothing.
 *
 * That also settles the first half. `caos-topbar` is the only manifest in the tree declaring this
 * source, on an input the SHIPPED manifest names `user_name`, and the host holds exactly the facts
 * that shell runtime holds about the person reading. `viewerInputContext` is the one place those two
 * spellings meet, so the vocabulary is the shell's own viewer rather than a set of keys chosen here.
 *
 * READ THE GENERATED MANIFEST AND NOT THE DOCS FILE, which is the mistake this shipped with once.
 * `caos-ui/src/components/topbar.docs.ts` names the input `userName` — the JS property the piece
 * observes — and `gen-component-manifests.mjs` emits it as `apiNameOf(input.name)`, so what an org
 * installs is `user_name`. `InputName` forbids uppercase outright, so a camelCase key here could
 * never match any declared input on any component.
 *
 * NOT ROLES, deliberately. `runtime.user.roles` is the other thing that record carries and it is
 * not offered: role-driven rendering is a permission question, answered by `heldPermissions` and
 * the object permission planes, and smuggling it in as a component input would give a page author a
 * second, unchecked way to ask it.
 *
 * CAOS-1806 — AND `recordField` IS THE THIRD, on the reading below. It and `userContext` landed
 * within a day of each other, so neither is "the second" on its own; the count is three of the
 * declared six answered here, and {@link RESOLVED_INPUT_SOURCES} is derived rather than written
 * down precisely so that number cannot be asserted wrongly in a comment like this one.
 *
 * WHAT A `recordField` INPUT READS: the field of the bound record whose api name is the INPUT'S OWN
 * `name`. That is the exact symmetry with `urlParameter` mapping `name` to `routeParams[name]`, and
 * both vocabularies are lower_snake, so no translation is invented between them. The ticket asked
 * for this reading; what follows is why the evidence against it does not hold, because that evidence
 * stopped two separate attempts and would otherwise stop a third.
 *
 * THE TWO SHIPPED DECLARATIONS ARE NOT COUNTER-EXAMPLES. `lookup_field.value` and
 * `record_header.heading` are the only manifests declaring this source, and neither names a field
 * any object carries — no object has one api-named `value` or `heading`. That was read as proving
 * the convention false. It does not, because NEITHER OF THEM CAN BE SERVED BY A FIELD READ OF ANY
 * KIND, so neither is evidence about which field convention to choose:
 *
 *   `record_header.heading` wants the record's DISPLAY NAME, which is not a field value. The
 *   incumbent is {@link recordDisplayName} (`content-source.ts`), assigned imperatively where that
 *   header is built: it runs the object's `nameTemplate` when there is one, falls back to
 *   "<Label> <shortId>" when the name field is empty, and prefixes the object label for an
 *   AutoNumber. A selector naming `nameField` — the shape previously proposed for this input —
 *   reproduces none of those three and would ship a heading that disagrees with every record
 *   header on screen today.
 *
 *   `lookup_field.value` is `type: recordId`, "the id of the record pointed at, written back when
 *   an option is chosen". It is a two-way binding to the lookup field the placement is EDITING, a
 *   fact about where the piece sits rather than about the record behind the page. No manifest can
 *   carry it and no read of the bound record can answer it.
 *
 * SO NOTHING REGRESSES AND NOTHING IS QUIETLY DECIDED FOR THEM. Both still resolve to nothing after
 * this change, because the records genuinely carry no such field and an absent field is left absent
 * — which is exactly what they get today, and both components keep working because the host builds
 * them imperatively rather than through this path. What they are owed is their own `source`
 * corrected; that is a change to shipped manifests and is not smuggled in here.
 *
 * THE RAW VALUE, NOT THE DISPLAY ONE. `PageRenderOptions.record` carries a `display` map of
 * host-formatted text beside the record's own keys, and the abandoned spike for this ticket
 * (`origin/w1-159-recordfield`) read `display` first on the argument that a component asking for a
 * field should see what the reader sees, as a placed FIELD on this same surface does. That is a
 * real precedent and it does not transfer, because an input is not a placed field: it declares a
 * `type`, and `display` is a string whatever the type says. A `number` input would receive
 * "$4,820.00" and a `recordId` input would receive the looked-up record's NAME instead of its id —
 * which is precisely the value `lookup_field.value` does not want. Deploy already refuses a
 * placement that sets a string for a `number` (CAOS-604), so resolving `display` here would have
 * the renderer supply values deploy would have rejected from an author.
 *
 * AN EMPTY VALUE IS DELIVERED, not skipped — the same spike skipped `''` and `null`. Skipping them
 * means a page's hard-coded value survives for a field the record genuinely holds as empty, so the
 * screen shows a stale authored number while the record says blank. It also breaks the symmetry the
 * whole convention rests on: `urlParameter` applies an empty route parameter rather than falling
 * back. Only a field the record does not carry AT ALL is absent, which is the distinction the
 * ticket asks for.
 *
 * WHAT `null` DOES, stated exactly, because a review found this paragraph previously claimed an
 * absent-vs-empty distinction that `null` does not actually exhibit. A record CAN hold a field as
 * `null` — an unset lookup or date — and that is a field the record carries, so it is delivered and
 * the declared source wins over a static value the page set. {@link applyProps} then renders `null`
 * as NO ATTRIBUTE, exactly as it does for an authored `null`, so on the element a null field is
 * indistinguishable from an absent one.
 *
 * That is deliberate and it is not this resolver's decision: `applyProps` treats `null` and
 * `undefined` alike for every input on every path, and a page author hard-coding `null` has always
 * got the same nothing. What differs here is only WHOSE value is lost — an absent field leaves the
 * author's static value standing, and a null one overrides it. Both are the declared source
 * winning: the record says the field is unset, and drawing the author's stale number instead would
 * be showing a value the record does not hold.
 */
interface InputSourceContext {
  readonly routeParams: Readonly<Record<string, string>> | undefined;
  /** CAOS-1809 — the facts about the person reading, keyed as placed inputs name them. */
  readonly viewer: Readonly<Record<string, string>> | undefined;
  /** CAOS-1806 — the record the surface is bound to, when it was opened for one. */
  readonly record: Readonly<Record<string, unknown>> | undefined;
}

/**
 * CAOS-1806 — the one key on {@link PageRenderOptions.record} that is NOT one of the record's
 * fields. Named here rather than written as a literal in the resolver so the two cannot drift: the
 * sidecar is declared on that type and read by `renderFieldItem`, and this is the only other place
 * that has to know it is not a field.
 */
const RECORD_DISPLAY_SIDECAR = 'display';

/**
 * The input sources this renderer can actually answer, and how it answers each.
 *
 * A resolver returns `undefined` for "this surface cannot supply that value", which is left ABSENT
 * on the element rather than written as empty — the CAOS-798 rule above, now the table's contract
 * rather than one branch's behaviour.
 */
const INPUT_SOURCE_RESOLVERS: ReadonlyMap<
  string,
  (name: string, ctx: InputSourceContext) => unknown
> = new Map([
  ['urlParameter', (name: string, ctx: InputSourceContext) => ctx.routeParams?.[name]],
  /**
   * CAOS-1809 — `Object.hasOwn` and not a bare index, for the hazard the `Map` comment above
   * describes one level further down. An input `name` is author-supplied and the kernel accepts any
   * bare identifier there, `constructor` included — `InputName` applies `BARE_IDENTIFIER_PATTERN`
   * and not the reserved-word set that guards object and field api names. Indexing a plain object
   * with that name would hand back something off `Object.prototype` and assign a function to the
   * element as a value.
   *
   * It is also the only correct reading of "nobody is signed in": a viewer fact this session does
   * not have must be ABSENT, so the component sees the same nothing it sees above a sign-in screen,
   * which is what `caos-topbar`'s own input documentation says that state looks like. An inherited
   * key is not a fact about the viewer.
   */
  [
    'userContext',
    (name: string, ctx: InputSourceContext) =>
      ctx.viewer !== undefined && Object.hasOwn(ctx.viewer, name) ? ctx.viewer[name] : undefined,
  ],
  /**
   * CAOS-1806 — `Object.hasOwn` rather than a bare index, for the hazard the `Map` comment above
   * describes one level further down. An input `name` is author-supplied and the kernel accepts any
   * lower_snake identifier there, which includes `constructor` — `InputName` applies
   * `BARE_IDENTIFIER_PATTERN` and NOT the `RESERVED_IDENTIFIERS` set that guards object and field
   * api names (CAOS-1354). A record is a plain object parsed from the API, so indexing it with that
   * name would hand back `Object` itself and assign a function to the element as a value. An own
   * key is also the only correct reading: a field the record does not carry must be ABSENT, and an
   * inherited one is not a field the record carries.
   *
   * AND `display` IS NOT A FIELD, which an own-key check alone does not know. Raised in review.
   * {@link PageRenderOptions.record} carries the host's formatted text as a `display` sidecar ON THE
   * SAME OBJECT as the record's own keys, so `Object.hasOwn(record, 'display')` is true and a
   * `recordField` input named `display` would be handed the whole formatted map — contradicting the
   * "raw value, not the display one" rule stated above, on the one input name where that rule is
   * easiest to violate. `display` is a legal {@link InputName}, so nothing upstream prevents it.
   * No shipped manifest declares such an input today; the guard is here because the collision is a
   * property of the record's shape rather than of any manifest, so it cannot be fixed by inspecting
   * the ones that exist.
   */
  [
    'recordField',
    (name: string, ctx: InputSourceContext) =>
      ctx.record !== undefined && name !== RECORD_DISPLAY_SIDECAR && Object.hasOwn(ctx.record, name)
        ? ctx.record[name]
        : undefined,
  ],
]);

/**
 * CAOS-1808 — the source names {@link INPUT_SOURCE_RESOLVERS} answers for, derived from the table
 * so the two cannot disagree. Read by `kernel/test/unit/caos-1808-component-input-sources.test.ts`
 * and by nothing at runtime: this is a statement about the host, never a check against the
 * kernel's enum.
 */
export const RESOLVED_INPUT_SOURCES: readonly string[] = [...INPUT_SOURCE_RESOLVERS.keys()];

function withDeclaredSources(
  props: Readonly<Record<string, unknown>> | undefined,
  installed: { readonly inputs?: readonly { readonly name: string; readonly source?: string }[] },
  ctx: InputSourceContext,
): Readonly<Record<string, unknown>> | undefined {
  const declared = (installed.inputs ?? []).flatMap((i) => {
    const resolve = i.source === undefined ? undefined : INPUT_SOURCE_RESOLVERS.get(i.source);
    return resolve === undefined ? [] : [{ name: i.name, resolve }];
  });
  if (declared.length === 0) return props;
  const out: Record<string, unknown> = { ...(props ?? {}) };
  let filled = false;
  for (const input of declared) {
    const value = input.resolve(input.name, ctx);
    if (value === undefined) continue;
    out[input.name] = value;
    filled = true;
  }
  return filled ? out : props;
}

/** CAOS-159 — the declaration a caller without a component manifest supplies: nothing is property-only. */
const EMPTY_PROPERTY_ONLY: ReadonlySet<string> = new Set<string>();

/**
 * CAOS-159 — hand the assign-only values over AGAIN, once the element has a class behind it.
 *
 * THE HAZARD THIS EXISTS FOR. Assigning a property to an element whose tag is not yet defined
 * creates an OWN property on the instance. When the element later upgrades, that own property
 * SHADOWS the accessor the class installs on its prototype — an own property always wins over a
 * prototype accessor — so the component's setter never runs and its backing field keeps its default
 * forever. The value looks delivered and is inert. None of the library's components implements the
 * lazy-upgrade pattern that would absorb this on their side.
 *
 * WHY IT IS DONE IN THREE STEPS. `upgrade` is what makes this reliable rather than hopeful: a
 * detached element does not upgrade when its tag is defined, only when it is next connected, which
 * may be long after this promise settles. `customElements.upgrade` performs the prototype swap now,
 * for this element, synchronously. Then deleting the own property is what UNCOVERS the accessor, and
 * only then does the re-assignment reach the setter.
 *
 * It is guarded on the API being present because the host runs its surfaces under a DOM
 * implementation in tests, and a missing `upgrade` should cost the re-delivery, not the render.
 *
 * NOT NEEDED ON THE OTHER RUNG. A tag that is already registered gives `createElement` an upgraded
 * instance, so the first assignment reaches the setter directly and there is nothing to uncover.
 */
function redeliverAssignOnly(
  el: HTMLElement,
  props: Readonly<Record<string, unknown>> | undefined,
  propertyOnly: ReadonlySet<string>,
): void {
  if (!props || propertyOnly.size === 0) return;
  const target = el as unknown as Record<string, unknown>;

  // WHAT IS RE-DELIVERED IS WHAT THE ELEMENT CURRENTLY HOLDS, NOT WHAT RENDER RESOLVED. `props` is
  // a snapshot taken when the surface drew, and between then and the module arriving the own
  // property is writable by anyone — `decorate.beforeProps`, a caller holding the element, a later
  // redraw. Re-asserting the snapshot would silently undo a fresher write, and the symptom would be
  // a value that appears correct and then reverts the instant the component's code finishes
  // loading, which is close to undebuggable from the outside.
  //
  // AND IT IS READ BEFORE `upgrade`, WHICH IS THE PART THAT IS EASY TO GET BACKWARDS. Upgrading runs
  // the component's constructor, and a constructor that does `this.foo = <default>` creates an own
  // property of its own. Read afterwards, that default is indistinguishable from a caller's write
  // and would win over the value this function exists to deliver. Read first and it cannot.
  const pending = new Map<string, unknown>();
  for (const [name, value] of Object.entries(props)) {
    if (value === undefined || value === null) continue;
    if (!propertyOnly.has(name)) continue;
    const property = propertyName(name);
    pending.set(
      property,
      Object.prototype.hasOwnProperty.call(target, property) ? target[property] : value,
    );
  }
  if (pending.size === 0) return;

  const registry = typeof customElements === 'undefined' ? undefined : customElements;
  if (typeof registry?.upgrade === 'function') registry.upgrade(el);
  for (const [property, value] of pending) {
    delete target[property];
    target[property] = value;
  }
}

/**
 * CAOS-159 — the inputs this component declared it can only be ASSIGNED, never written as markup.
 *
 * `propertyOnly` is the manifest saying "I observe no attribute for this". Absent ⇒ the input can be
 * written as an attribute, which is what every manifest said before CAOS-653 added the field, so a
 * component that declares nothing is treated exactly as it was.
 */
function propertyOnlyInputs(installed: {
  readonly inputs?: readonly { readonly name: string; readonly propertyOnly?: boolean }[];
}): ReadonlySet<string> {
  const out = new Set<string>();
  for (const input of installed.inputs ?? []) if (input.propertyOnly === true) out.add(input.name);
  return out;
}

/**
 * CAOS-159 — WHY A SCALAR IS THE WHOLE POINT OF `propertyOnly`, and why this read was missing.
 *
 * A list or an object was never affected: an attribute is a string and cannot hold one, so those
 * already fell through to the property assignment at the bottom of this function and arrived. A
 * STRING or a NUMBER gives every appearance of being writable as markup, takes the branch above it,
 * and is set as an attribute the component does not observe — so the author sets a value, gets no
 * error, and the component receives nothing. That is the failure `propertyOnly` was added to
 * describe (`kernel/src/meta/component.ts`, CAOS-653: "a placement that renders it as one sets
 * nothing and the author gets no error, only a value that never arrives"), and until now nothing
 * in the host read the declaration.
 *
 * MEASURED, so the blast radius is a number rather than an impression: of the 81 inputs in the
 * compiled standard registry carrying `propertyOnly`, 73 are `json` and were already arriving. The
 * eight that were not are `card_grid.batch_size`, `difference_modal.source_text` and
 * `.target_text`, `field_renderer.ref_display` and `.ref_href`, `listing_card.rating_avg` and
 * `.install_count`, and `log_viewer.raw`.
 *
 * NOTHING CAN REGRESS BY THIS CHANGE, and that is checkable rather than hopeful: an input only
 * takes the new path if its own manifest declared `propertyOnly`, and none of those five components
 * observes the matching attribute — `difference_modal` observes `item`, `source-name`,
 * `target-name`, `missing` and `closable`; `field_renderer` observes `mode`, `masked` and
 * `required`; `log_viewer` observes `category`, `search` and `empty-text`; `listing_card` observes
 * eight attributes, none of them `rating-avg` or `install-count`; `card_grid` carries a
 * `batchSize` property setter and observes no attribute for it. So the attribute being set today is
 * read by nobody, and the value it carried arrived nowhere.
 *
 * A BOOLEAN IS ROUTED THE SAME WAY rather than kept on the present-or-absent branch. No shipped
 * manifest pairs `propertyOnly` with a boolean today, so this changes nothing now; it is written
 * this way because the declaration is about where the value is READ, not about what kind it is, and
 * a component that declares a property-only yes/no would otherwise get `removeAttribute` on an
 * attribute it never had and never be told `false`.
 *
 * `applyProps` IS ALSO CALLED WITHOUT A COMPONENT (the `figure` on a host-drawn region), and that
 * caller has no declaration to pass, so it passes {@link EMPTY_PROPERTY_ONLY} and every value takes
 * the path it always did. The parameter is REQUIRED rather than defaulted on purpose: defaulted, a
 * call site added later would silently get no property-only behaviour and the values it was asked to
 * deliver would go back to arriving nowhere, which is the exact failure this function was changed to
 * end. Required, forgetting it does not compile.
 */
function applyProps(
  el: HTMLElement,
  props: Readonly<Record<string, unknown>> | undefined,
  propertyOnly: ReadonlySet<string>,
): void {
  if (!props) return;
  for (const [name, value] of Object.entries(props)) {
    if (value === undefined || value === null) continue;
    if (propertyOnly.has(name)) {
      (el as unknown as Record<string, unknown>)[propertyName(name)] = value;
      continue;
    }
    if (typeof value === 'boolean') {
      // A yes/no reads as present-or-absent, the way every boolean attribute in HTML does.
      if (value) el.setAttribute(attributeName(name), '');
      else el.removeAttribute(attributeName(name));
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      el.setAttribute(attributeName(name), String(value));
      continue;
    }
    (el as unknown as Record<string, unknown>)[propertyName(name)] = value;
  }
}

/** `empty_label` or `emptyLabel` → `empty-label`: the attribute spelling of an input's declared name. */
function attributeName(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').replace(/_/g, '-').toLowerCase();
}

/** `empty_label` → `emptyLabel`: the property spelling of an input's declared name. */
function propertyName(name: string): string {
  return name.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
}
